import {
  differenceInDays,
  differenceInMinutes,
  differenceInHours,
  differenceInMonths,
  differenceInYears,
  differenceInSeconds,
  addDays,
  isBefore,
  isEqual,
  format,
  isAfter,
  isToday,
  isYesterday,
  formatDistanceToNow,
  isSameDay,
  differenceInCalendarDays,
  endOfDay,
  subDays,
  subHours,
  subMinutes,
  differenceInWeeks,
  isValid,
} from 'date-fns';
import type { Theme } from '@mui/material/styles';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ArrowCircleRightOutlinedIcon from '@mui/icons-material/ArrowCircleRightOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import InstagramColoredIcon from 'src/assets/icons/InstagramColored.svg';
import LinkedinColoredIcon from 'src/assets/icons/LinkedInColored.svg';
import FacebookColoredIcon from 'src/assets/icons/FacebookColored.svg';
import TwitterColoredIcon from 'src/assets/icons/TwitterColored.svg';
import WhatsappColoredIcon from 'src/assets/icons/WhatsAppColored.svg';
import EmailColoredIcon from 'src/assets/icons/EmailColored.svg';
import type {
  CreatePlanResponse,
  GetOrganizationListSub,
  GetCartResponse,
  UpsertAddressResponse,
  UpsertShippingAddressBody,
  GetAllCompetitionsSub,
  GetCompetitionListCardSub,
  AttemptHistory,
  GetAllDomainMockTestSub,
  ProfileMatchParam,
  ApplicantListParam,
  SkillIDNameWeight,
  CandidateJobDetails,
  GetCompetitionQuestionsSub,
  MultiplayerSessionProgressResponse,
  MultiplayerSessionStartStatisticsResponse,
  CalibrationStatusResponse,
} from 'src/openapi';
import type {
  IEducation,
  IExperience,
  IProject,
} from 'src/components/candidateProfile/types';
import {
  EmployerPlanType,
  HeaderDeviceType,
  ORG_STATUS_PROMT_DATA,
  PLAN_UNLIMITED_COUNT,
  PLAY_STREAK_FEEDBACK,
  SALARY_FREQUENCY,
  SkillTestCardButtonType,
} from 'src/constants';
import {
  LS_KEY_DOWNLOAD_ORIGINAL_APPLICANT_RESUME,
  LS_KEY_HIDE_CONTACT_DETAILS_FROM_RESUME,
  LS_KEY_IS_COMPETITIONS_AUDIO_ENABLED,
  LS_KEY_IS_SKILL_BUILDER_AUDIO_ENABLED,
  LS_KEY_SHOW_DB_RESUME_DOWNLOAD_CONFIRMATION,
  LS_KEY_WHITE_LABEL_RESUME,
  LS_KEY_WHITE_LABEL_RESUME_TYPE,
} from 'src/constants/localStorageKeys';
import { CURRENCY_SYMBOLS, INR_ALIASES } from 'src/constants/currency';
import countries from 'src/constants/CountryData';
import type { OrgStatusData } from 'src/pages/postedJobList/types';
import type {
  CheckoutDetails,
  TransformedCartData,
} from 'src/redux/types/store';
import type {
  ButtonData,
  CompetitionTimeState,
  HiringAgentState,
} from 'src/pages/competition/types';
import type { FeedbackMessage } from 'src/components/play/types';
import type { ResumeDownloadSettings } from 'src/redux/types/employerLayout';
import { matchPath } from 'react-router-dom';
import type { InviteApplicantPayloadParams } from 'src/redux/types/inviteApplicant';
import type { InputFilters, MinMax, Option } from 'src/pages/jobDetails/types';
import { PERCENTILE_MIN_POOL } from 'src/constants/skillRank';
import { removeCommas } from 'src/pages/jobPosting/components/salaryFormat';
import type {
  AttemptNotifResponse,
  MultiplayerPhase,
} from 'src/pages/skillBuilder/types/Multiplayer';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { getFormattedErr } from '.';

type JobTitleSource = {
  title?: string | null;
  job_role_details?: { name?: string | null } | null;
  job_role_name?: string | null;
} | null;

/**
 * A job's display title. `title` is the source of truth; the canonical role
 * name is only a fallback for API shapes that omit `title`. The role
 * (`job_role_details`) stays the matching taxonomy — never the display label.
 */
export const getJobTitle = (job?: JobTitleSource): string =>
  job?.title || job?.job_role_details?.name || job?.job_role_name || '';

type GetYearProps = {
  numberOfYears: number;
  type: 'next' | 'prev';
};

export const getMonthAndDate = (currDate?: Date) => {
  const date = currDate || new Date();
  const daysOfWeek = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const dayOfWeek = daysOfWeek[date.getDay()];
  const month = months[date.getMonth()];
  const dayOfMonth = date.getDate();

  return `${dayOfWeek}, ${month} ${dayOfMonth}`;
};

export const parseUTCDate = (date?: string | null): Date | null => {
  if (!date) return null;
  let normalized = date.replace(' ', 'T');
  if (!normalized.includes('Z') && !normalized.includes('+')) {
    normalized += 'Z';
  }
  const parsed = new Date(normalized);
  return isValid(parsed) ? parsed : null;
};

export interface EmbeddablePreview {
  kind: 'youtube' | 'vimeo' | 'loom' | 'drive';
  embedUrl: string;
}

/**
 * Maps a user-pasted URL to an inline-playable embed URL for the known video
 * hosts (YouTube, Vimeo, Loom, Google Drive). Returns null for anything else so
 * the caller falls back to a plain link. Derivation is purely from the URL — no
 * network calls — so it's safe to run client-side.
 */
export const getEmbeddablePreview = (
  rawUrl?: string | null,
): EmbeddablePreview | null => {
  if (!rawUrl) return null;
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return null;
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
  const host = url.hostname.replace(/^www\./, '');

  if (['youtube.com', 'm.youtube.com', 'youtube-nocookie.com'].includes(host)) {
    let id = url.searchParams.get('v');
    if (!id) {
      const m = url.pathname.match(/\/(?:shorts|embed|live)\/([\w-]+)/);
      if (m) [, id] = m;
    }
    if (id)
      return {
        kind: 'youtube',
        embedUrl: `https://www.youtube.com/embed/${id}`,
      };
  }
  if (host === 'youtu.be') {
    const id = url.pathname.split('/').filter(Boolean)[0];
    if (id)
      return {
        kind: 'youtube',
        embedUrl: `https://www.youtube.com/embed/${id}`,
      };
  }
  if (host === 'vimeo.com') {
    const id = url.pathname.split('/').filter(Boolean)[0];
    if (id && /^\d+$/.test(id)) {
      return {
        kind: 'vimeo',
        embedUrl: `https://player.vimeo.com/video/${id}`,
      };
    }
  }
  if (host === 'loom.com') {
    const m = url.pathname.match(/\/(?:share|embed)\/([\w-]+)/);
    if (m)
      return { kind: 'loom', embedUrl: `https://www.loom.com/embed/${m[1]}` };
  }
  if (host === 'drive.google.com') {
    const m = url.pathname.match(/\/file\/d\/([\w-]+)/);
    const id = m ? m[1] : url.searchParams.get('id');
    if (id)
      return {
        kind: 'drive',
        embedUrl: `https://drive.google.com/file/d/${id}/preview`,
      };
  }
  return null;
};

/** "1.8 MB", or "640 KB" under a megabyte; empty when the size is unknown. */
export const formatFileSize = (bytes?: number | null): string => {
  if (!bytes) return '';
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
};

export const timeAgo = (date?: string | null): string => {
  const parsed = parseUTCDate(date);
  if (!parsed) return '—';
  const seconds = Math.floor((Date.now() - parsed.getTime()) / 1000);
  if (seconds < 45) return 'just now';
  // 45 to 59 seconds rounds down to 0, which read "0 mins ago".
  const minutes = Math.max(1, Math.floor(seconds / 60));
  if (minutes < 60) return `${minutes} min${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
  return `${Math.floor(months / 12)} year${months < 24 ? '' : 's'} ago`;
};

export const formatFullDateTime = (date?: string | null): string => {
  const parsed = parseUTCDate(date);
  if (!parsed) return '—';
  return parsed.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const getCurrentTimePeriod = () => {
  const now = new Date();
  const hour = now.getHours();

  if (hour < 12) {
    return 'morning';
  }
  if (hour < 17) {
    return 'afternoon';
  }
  return 'evening';
};

export const capitalizeText = (str: string = 'user') =>
  str.charAt(0).toUpperCase() + str.slice(1);

const getYears = ({ numberOfYears, type }: GetYearProps) => {
  let currentYear = new Date().getFullYear();
  const years: number[] = [];
  let i = numberOfYears;
  while (i > 0) {
    years.push(currentYear);
    if (type === 'next') {
      currentYear += 1;
    } else {
      currentYear -= 1;
    }
    i -= 1;
  }
  return years.sort((a, b) => a - b);
};

// `user.country` comes back as an ISO alpha-2 code; CountryData already carries
// those inside its dialling labels ("IN(+91)"), so reuse it instead of shipping
// a second country table.
export const getCountryNameFromCode = (code?: string | null) => {
  if (!code) {
    return '';
  }
  if (code.length !== 2) {
    return code;
  }
  const match = countries.find((country) =>
    country.label.toUpperCase().startsWith(`${code.toUpperCase()}(`),
  );
  return match?.name ?? code;
};

export const roundToFractionDigits = (
  value: number,
  fractionDigits: number,
) => {
  const factor = 10 ** fractionDigits;
  return Math.round(value * factor) / factor;
};

export function formatCTC(ctc: number) {
  if (ctc < 100000) {
    return `${roundToFractionDigits(ctc / 1000, 2)}K`;
  }

  if (ctc < 10000000) {
    return `${roundToFractionDigits(ctc / 100000, 2)}L`;
  }

  return `${roundToFractionDigits(ctc / 10000000, 1)}Cr`;
}

export function removeHtmlTags(str: string) {
  return str.replace(/<\/?[^>]+(>|$)/g, '');
}

/** Where one block ends and the next begins. A rich-text editor reads its
 * document as lines, so `<p>a</p><p>b</p>` is the three characters "a\nb" --
 * stripping the tags alone runs the two paragraphs together into "ab". */
const BLOCK_BOUNDARY = /<\/(p|div|li|h[1-6]|blockquote|pre)>|<br\s*\/?>/gi;

/** Plain text as a person reads it, and as the editor counts it.
 *
 * Two things the markup hides, and both of them showed up as a character
 * counter disagreeing with the editor it sat under. Entities are literal in
 * the source, so an apostrophe counted as the six characters of `&apos;` and a
 * pasted space as the six of `&nbsp;` -- a 652-character description read as
 * 811. Block boundaries are punctuation the tags carry rather than text, so
 * dropping them read a description the editor had already closed at 500 as
 * 497: the counter still showed room while the keyboard did nothing.
 *
 * The trailing newline goes with them, because every block ends with one and
 * nobody typed the last. */
export function htmlToPlainText(str: string): string {
  const withoutTags = removeHtmlTags((str ?? '').replace(BLOCK_BOUNDARY, '\n'));
  if (typeof document === 'undefined') return withoutTags.replace(/\n$/, '');
  // textarea parses its content as raw text, so this decodes entities without
  // interpreting any markup.
  const el = document.createElement('textarea');
  el.innerHTML = withoutTags;
  return el.value.replace(/\u00a0/g, ' ').replace(/\n$/, '');
}

/** Characters as the editor counts them -- what a `maxLength` is measured on,
 * so a counter or a validator built on this agrees with what the writer is
 * actually allowed to type. */
export const richTextLength = (html?: string | null) =>
  htmlToPlainText(html ?? '').length;

export default getYears;

export const isWithinPast48Hours = (givenDate: string | Date) => {
  if (!givenDate) return false;
  const date =
    typeof givenDate === 'string' ? parseUTCDate(givenDate) : givenDate;
  if (!date) return false;
  return differenceInHours(new Date(), date) <= 48;
};

// Derives from theme palette tokens (rather than fixed hex) so every status
// color adapts in dark mode — e.g. 'Concluded' used to stay the light theme's
// navy primary even in dark mode, reading as a washed-out, low-contrast chip.
export const getJobStatusColor = (statusState: String, theme: Theme) => {
  if (statusState === 'Active') {
    return theme.palette.success.main;
  }
  if (statusState === 'Paused') {
    return theme.palette.warning.light;
  }
  if (statusState === 'Draft') {
    return theme.palette.text.secondary;
  }
  if (statusState === 'UnderReview') {
    return theme.palette.warning.main;
  }
  if (statusState === 'Closed') {
    return theme.palette.error.main;
  }
  if (statusState === 'Concluded') {
    return theme.palette.primary.main;
  }
  if (statusState === 'Rejected') {
    return theme.palette.error.dark;
  }
  return theme.palette.warning.light;
};

export const formatDateRange = (
  date1: Date,
  date2: Date,
  excludeYear: boolean = false,
) => {
  const getFormatted = (date: Date) => {
    if (isSameDay(date, new Date())) {
      return 'Today';
    }
    if (isSameDay(addDays(date, 1), new Date())) {
      return 'Yesterday';
    }
    if (isSameDay(subDays(date, 1), new Date())) {
      return 'Tomorrow';
    }
    return excludeYear ? format(date, 'dd MMM') : format(date, 'dd MMM yy');
  };

  if (isSameDay(date1, date2)) {
    return getFormatted(date1);
  }
  return `${getFormatted(date1)} - ${getFormatted(date2)}`;
};

export const localTimeToUtcHHMM = (time?: string): string | undefined => {
  if (!time) return undefined;
  const [hh, mm] = time.split(':').map((v) => Number(v ?? 0));
  if (Number.isNaN(hh) || Number.isNaN(mm)) return undefined;
  const d = new Date();
  d.setHours(hh, mm, 0, 0);
  const utcHH = String(d.getUTCHours()).padStart(2, '0');
  const utcMM = String(d.getUTCMinutes()).padStart(2, '0');
  return `${utcHH}:${utcMM}`;
};

export const convertToUTC = (date: Date | string): string => {
  if (!date) return '';
  const parsed =
    typeof date === 'string' ? (parseUTCDate(date) ?? new Date(date)) : date;
  if (!(parsed instanceof Date) || isNaN(parsed.getTime())) return '';
  return parsed.toISOString();
};

export const formatActivityStatus = (status: string): string => {
  switch (status) {
    case 'JobPosted':
      return 'Team Activity - Job Posted';
    case 'JobClosed':
      return 'Team Activity - Job Closed';
    case 'JobConcluded':
      return 'Team Activity - Job Concluded';
    case 'ShortlistedCandidate':
      return 'Job Activity - Shortlisted';
    case 'RejectedCandidate':
      return 'Job Activity - Rejected';
    case 'HiredCandidate':
      return 'Job Activity - Hired';
    case 'UnShortlistedCandidate':
      return 'Job Activity - UnShortlisted';
    case 'UnRejectedCandidate':
      return 'Job Activity - UnRejected';
    case 'UnHiredCandidate':
      return 'Job Activity - UnHired';
    case 'CandidateTagged':
      return 'Job Activity - Candidate Tagged';
    case 'MessagedCandidate':
      return 'Job Activity - Messaged Candidate';
    case 'BulkMessageSent':
      return 'Job Activity - Bulk Message Sent';
    case 'ResumeDownloaded':
      return 'Resume Downloaded';
    case 'ResumeAnalysed':
      return 'Job Activity - Resume Analyzed';
    case 'BulkResumeAnalysed':
      return 'Job Activity - Bulk Resume Analyzed';
    case 'RatedCandidate':
      return 'Job Activity - Rated Candidate';
    case 'NewMemberInvited':
      return 'Team Activity - New Member Invited';
    case 'ReviewedCandidate':
      return 'Job Activity - Reviewed Candidate';
    case 'AssessmentScored':
      return 'Job Activity - Assessment Scored';
    case 'JobApplied':
      return 'Job Activity - Job Applied';
    case 'JobViewed':
      return 'Job Activity - Job Viewed';
    case 'JobShared':
      return 'Job Activity - Job Shared';
    case 'SelfResumeAnalysed':
      return 'Job Activity - Self Resume Analyzed';
    case 'ApplicantUnlocked':
      return 'Job Activity - Applicant Unlocked';
    case 'AiAnalysisUnlocked':
      return 'Job Activity - AI Analysis Unlocked';
    case 'InterviewScheduled':
      return 'Interview - Scheduled';
    case 'InterviewRescheduled':
      return 'Interview - Rescheduled';
    case 'InterviewCancelled':
      return 'Interview - Cancelled';
    case 'InterviewResponded':
      return 'Interview - Candidate Responded';
    case 'InterviewCompleted':
      return 'Interview - Completed';
    case 'InterviewRecordingUnlocked':
      return 'Interview - Recording Unlocked';
    default:
      return status;
  }
};

export const convertUTCToLocalTime = (utcTime: string): string => {
  const date = parseUTCDate(utcTime);
  return date ? format(date, 'yyyy-MM-dd HH:mm:ss') : '';
};

export const formatTo12Hour = (date: string): string => {
  const parsed = parseUTCDate(date);
  return parsed ? format(parsed, 'hh:mma') : '';
};

export const checkAndResetTodaysDate = (dateString: string): string => {
  const inputDate = new Date(dateString);
  const today = new Date();

  today.setHours(0, 0, 0, 0);
  inputDate.setHours(0, 0, 0, 0);

  if (inputDate.getTime() === today.getTime()) {
    return `${today.toISOString().split('T')[0]}T00:00:00Z`;
  }
  return dateString;
};

export const isValidUrl = (url: string): boolean => {
  if (url.length === 0) return false;
  try {
    // eslint-disable-next-line no-new
    new URL(url);
    return true;
  } catch (e) {
    return false;
  }
};
type Output<T> = {
  date: string;
  entries: T[];
};

export const formatDate = (
  dateString: string,
  formatStr: string = 'do MMMM, yyyy',
  showRelativeForToday?: boolean,
): string => {
  if (isToday(dateString)) {
    if (showRelativeForToday) {
      return formatDistanceToNow(dateString, { addSuffix: true });
    }
    return 'Today';
  }
  if (isYesterday(dateString)) {
    return 'Yesterday';
  }
  return format(dateString, formatStr);
};

type GroupByDataType = {
  created_at?: string;
} & {
  [key: string]: any;
};

export const groupByDate = <T>(data: Array<GroupByDataType>): Output<T>[] => {
  const groupedData: { [key: string]: any[] } = {};

  data.forEach((item) => {
    const date = formatDate(item.created_at ?? new Date().toString());
    if (date) {
      if (!groupedData[date]) {
        groupedData[date] = [];
      }
      groupedData[date].push(item);
    }
  });

  return Object.keys(groupedData).map((date) => ({
    date,
    entries: groupedData[date],
  }));
};

// Pairs up consecutive categories (e.g. Jan+Feb, Mar+Apr) into one bucket —
// used on mobile to halve the number of x-axis categories in month-based
// charts so labels/bars have room to breathe on narrow screens.
//
// `combine` must match what the series measures. 'sum' suits per-period counts
// (questions answered in a month). A cumulative series — a running score total —
// needs 'last': adding two running totals doubles the figure, and an odd final
// bucket holds one month against everyone else's two, so it plunges.
export const combineMonthPairs = (
  labels: string[],
  values: number[],
  combine: 'sum' | 'last' = 'sum',
): { labels: string[]; values: number[] } => {
  const combinedLabels: string[] = [];
  const combinedValues: number[] = [];
  for (let i = 0; i < labels.length; i += 2) {
    const next = labels[i + 1];
    combinedLabels.push(next ? `${labels[i]}-${next}` : labels[i]);
    combinedValues.push(
      combine === 'last'
        ? (values[i + 1] ?? values[i] ?? 0)
        : (values[i] ?? 0) + (values[i + 1] ?? 0),
    );
  }
  return { labels: combinedLabels, values: combinedValues };
};

export const getDataBeforeTime = (
  data: Array<GroupByDataType>,
  count: number,
  unit: 'hours' | 'days' | 'minutes',
) => {
  const now = new Date();
  const cutoff =
    unit === 'hours'
      ? subHours(now, count)
      : unit === 'days'
        ? subDays(now, count)
        : subMinutes(now, count);

  return data.filter((val) => {
    const parsed = parseUTCDate(val.created_at ?? '');
    return parsed ? isAfter(parsed, cutoff) : false;
  });
};

export const isPdfFile = (filePath: string): boolean =>
  filePath.toLowerCase().split('?')[0].endsWith('.pdf');

export const isValidEmail = (tag: string): boolean =>
  /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i.test(
    tag,
  );
export const isValidPhone = (phone: string): boolean =>
  /^[6-9]\d{9}$/.test(phone);
export const isValidUserId = (id: string): boolean => {
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(id);
};

export const pushTag = (tag: string, tags: any, setTags: any) => {
  if (!tag) return;

  if (isValidEmail(tag)) {
    setTags([...tags, { value: tag, isValid: true }]);
  } else {
    setTags([...tags, { value: tag, isValid: false }]);
  }
};

export const getTagTrimmedEditorData = (data: string) =>
  data
    .replace(/&nbsp;/g, ' ')
    .replace(
      /(^([ ]*<(h[1-3]|ul|ol|p)><br><\/(h[1-3]|ul|ol|p)>[ ]*)+)|(([ ]*<(h[1-3]|ul|ol|p)><br><\/(h[1-3]|ul|ol|p)>[ ]*)+$)/gi,
      '',
    )
    .trim();

export const getDurationInDHMSFormat = (seconds: number) => {
  let s = seconds;

  const d = Math.floor(s / (24 * 60 * 60));
  s %= 24 * 60 * 60;

  const h = Math.floor(s / (60 * 60));
  s %= 60 * 60;

  const m = Math.floor(s / 60);
  s %= 60;

  if (d > 0) {
    return `${d}:${h}:${m}`;
  }

  if (h > 0) {
    return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }

  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

export const getStoredSkillBuilderSettings = () => ({
  [LS_KEY_IS_SKILL_BUILDER_AUDIO_ENABLED]: JSON.parse(
    localStorage.getItem(LS_KEY_IS_SKILL_BUILDER_AUDIO_ENABLED) ?? 'true',
  ),
});

export const getStoredCompetitionSettings = () => ({
  [LS_KEY_IS_COMPETITIONS_AUDIO_ENABLED]: JSON.parse(
    localStorage.getItem(LS_KEY_IS_COMPETITIONS_AUDIO_ENABLED) ?? 'true',
  ),
});

export const getFilledChatTemplateText = (
  templateText: string,
  dynamicValues: Record<string, string>,
) => {
  let filledTemplate = templateText;
  Object.keys(dynamicValues).forEach((key) => {
    filledTemplate = filledTemplate.replaceAll(
      `[${key}]`,
      dynamicValues[key] ?? '',
    );
  });

  return filledTemplate;
};

export const formatBulkTemplateMessage = (
  templateDynamicValues: Record<string, string>,
  message: string,
): string => {
  const keys = Object.keys(templateDynamicValues);
  let formattedMessage = message;
  keys.forEach((key) => {
    formattedMessage = formattedMessage.replaceAll(`[${key}]`, `{${key}}`);
  });
  return formattedMessage;
};

type S3UrlFileType = 'PDF' | 'IMAGE';

export const getFileTypeFromS3SignedUrl = (url: string): S3UrlFileType => {
  if (!url.startsWith('https')) {
    return 'IMAGE';
  }
  const urlObj = new URL(url);
  if (urlObj.pathname.endsWith('.pdf')) {
    return 'PDF';
  }
  return 'IMAGE';
};

export const isValidEducation = (education: IEducation) => {
  if (
    !(education.college_id || education.college_name) ||
    !(education.degree_id || education.degree_name) ||
    !(education.specialization_id || education.specialization_name) ||
    !(education.location_details || education.is_distance_learning) ||
    !education.start_date ||
    !(education.end_date || education.is_currently_studying)
  ) {
    return false;
  }
  return true;
};

export const isValidExperience = (experience: IExperience) => {
  if (
    !(experience.company_id || experience.company_name) ||
    !experience.employment_type ||
    !(experience.location_details || experience.is_remote) ||
    !experience.start_date ||
    !(experience.end_date || experience.is_current_company)
  ) {
    return false;
  }
  return true;
};

export const isValidProject = (project: IProject) => {
  if (
    !project.title ||
    !project.description ||
    (project.start_date && !(project.end_date || project.is_ongoing))
  ) {
    return false;
  }
  return true;
};

export const linkify = (text: string) => {
  // More robust URL pattern
  const urlPattern = /\b((https?|ftp):\/\/[^\s<>"']+)\b/gi;

  // Function to escape HTML characters
  const escapeHTML = (str: string) =>
    str.replace(
      /[&<>"']/g,
      (char) =>
        ({
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;',
        })[char] || char,
    );

  // Check if a word is a URL
  const isUrl = (word: string) => word.match(urlPattern);

  // Function to wrap URLs with anchor tags safely
  const addMarkup = (word: string) =>
    isUrl(word)
      ? `<a href="${encodeURI(word)}" style="word-break: break-word;" target="_blank" rel="noopener noreferrer">${escapeHTML(
          word,
        )}</a>`
      : escapeHTML(word); // Escape all non-URL words to prevent XSS

  // Preserve spaces & newlines
  const words = text.split(/(\s+)/);
  const formattedWords = words.map((w) => addMarkup(w));
  return formattedWords.join('');
};

export const splitPhoneNo = (
  phoneNo: string | undefined,
): { countryCode: string; number: string } => {
  const raw = phoneNo || '';
  // Split on the FIRST dash only — some calling codes in CountryData.ts are
  // themselves dash-separated (e.g. "+1-684"), so a naive split('-') would
  // chop the local number apart too.
  const dashIndex = raw.indexOf('-');
  const hasCode = dashIndex !== -1;
  const rawNumber = hasCode ? raw.slice(dashIndex + 1) : raw;
  const number = rawNumber.replace(/\D/g, '').slice(-10);
  const countryCode = hasCode
    ? raw.slice(0, dashIndex).replace(/\D/g, '') || '91'
    : '91';
  return { countryCode, number };
};

export const updateStorePaymentAddress = (address: UpsertAddressResponse) => {
  const { countryCode, number } = splitPhoneNo(address.shipping?.phone_no);
  return {
    name: address.shipping?.name || '',
    email: address.shipping?.email || '',
    phone: { number, country_code: countryCode },
    shippingAddress: {
      address: address.shipping?.address || '',
      pincode: address.shipping?.pincode || '',
      state: address.shipping?.state || '',
      country: address.shipping?.country || 'India',
    },
    billingAddress: {
      address: address.billing?.address || '',
      pincode: address.billing?.pincode || '',
      state: address.billing?.state || '',
      country: address.billing?.country || 'India',
    },
    billingSameAsShipping:
      address.billing?.address === address.shipping?.address,
  };
};

export const getAddresses = (
  data: CheckoutDetails,
): {
  shippingAddress: UpsertShippingAddressBody;
  billingAddress: UpsertShippingAddressBody;
} => {
  // Matches the "countryCode-number" format the backend stores/expects
  // (see send_whatsapp_otp in backend-api's sms/controller.py) — sending
  // just the local number here is what left old addresses without their
  // real country code on reload (splitPhoneNo defaulted to '91').
  const phoneNo = `${data.phone.country_code}-${data.phone.number}`;
  const shippingAddress: UpsertShippingAddressBody = {
    name: data.name,
    email: data.email,
    phone_no: phoneNo,
    address: data.shippingAddress.address,
    pincode: data.shippingAddress.pincode,
    state: data.shippingAddress.state,
    country: data.shippingAddress.country,
    address_type: 'shipping',
  };

  const billingAddress: UpsertShippingAddressBody = data.billingSameAsShipping
    ? { ...shippingAddress, address_type: 'billing' }
    : {
        name: data.name,
        email: data.email,
        phone_no: phoneNo,
        address: data.billingAddress.address,
        pincode: data.billingAddress.pincode,
        state: data.billingAddress.state,
        country: data.billingAddress.country,
        address_type: 'billing',
      };

  return { shippingAddress, billingAddress };
};

// How many gems (1 gem = ₹1) can be applied as a checkout-time discount
// against the currency total. Mixed carts already reserve `netGems` for a
// real gems-mode item — only the remainder is spendable again as a
// discount, matching the backend's identical cap in initiate_payment_ctrl
// (spendable_gems = current_gems - net_gems).
export const getGemsDiscountCap = (
  userGems: number | undefined,
  netCurrency: number,
  netGems: number,
): number => {
  const spendableGems = Math.max(0, (userGems || 0) - netGems);
  return Math.min(spendableGems, netCurrency);
};

export const transformCartData = (
  orderData: GetCartResponse[],
): TransformedCartData[] =>
  orderData.flatMap((order) =>
    order.cart
      ? order.cart?.map((cart) => ({
          ...order,
          cart_id: cart.id,
          cart_created_at: cart.created_at,
          gst_on_item: cart.gst_on_item,
          item: cart.item,
          item_id: cart.item_id,
          quantity: cart.quantity,
          total_amount_in_currency: cart.total_amount_in_currency,
          total_amount_in_gems: cart.total_amount_in_gems,
          transaction_id: cart.transaction_id,
          cart: undefined,
        }))
      : {
          ...order,
          cart: undefined,
          cart_id: undefined,
          cart_created_at: order.created_at,
          gst_on_item: undefined,
          item: undefined,
          item_id: undefined,
          quantity: undefined,
          total_amount_in_currency: undefined,
          total_amount_in_gems: undefined,
          transaction_id: undefined,
        },
  );

/** 320000/1000 → "320", 320500/1000 → "320.5" — one decimal, never a bare ".0". */
const scaledAmount = (salary: number, unit: number) =>
  `${Number((salary / unit).toFixed(1))}`;

/**
 * The display symbol for a currency code — "₹", "$", "C$", or the code itself
 * plus a space ("AED ") when there's no legible glyph for it. Never returns a
 * bare code with no separator, so callers can always concatenate.
 *
 * Payloads occasionally carry a symbol where a code is expected ("₹"), so
 * anything that isn't a three-letter code is passed through untouched.
 */
export const getCurrencySymbol = (currency?: string | null): string => {
  const code = (currency || 'INR').trim();
  if (!/^[A-Za-z]{3}$/.test(code)) return code;
  const upper = code.toUpperCase();
  return CURRENCY_SYMBOLS[upper] ?? `${upper} `;
};

/**
 * A currency for a *picker*, where the code has to stay visible: "₹ INR",
 * "$ USD" — "$" alone cannot tell USD from SGD. Currencies with no glyph
 * resolve to the code itself, so they render as plain "AED" rather than the
 * "AED  AED" a naive `symbol + code` produces (getCurrencySymbol pads them
 * with a trailing space so amount concatenation reads "AED 50K").
 */
export const getCurrencyLabel = (
  currency?: string | null,
  style: 'space' | 'paren' = 'space',
): string => {
  const code = (currency || '').trim();
  const symbol = getCurrencySymbol(code).trim();
  if (!code || symbol === code.toUpperCase()) return code;
  return style === 'paren' ? `${symbol}(${code})` : `${symbol} ${code}`;
};

/**
 * Salary as a short, scannable figure: the lakh/crore scale for INR, K/M/B
 * elsewhere. Always abbreviated — a six-digit figure spelled out ("₹65000",
 * "$320000") is hard to read at a glance and overflows the tiles and chips it
 * sits in, so no surface asks for the exact number.
 *
 * The code is resolved to a symbol rather than printed raw: "USD180k" is both
 * wider and harder to read than "$180K", and it disagreed with the public job
 * pages, which have always rendered the symbol.
 */
export const getFormattedSalary = (
  salary: number,
  currency: string = 'INR',
) => {
  const symbol = getCurrencySymbol(currency);
  const isINR = INR_ALIASES.includes((currency || 'INR').trim().toUpperCase());
  if (isINR) {
    if (salary >= 10000000) {
      return `${symbol}${scaledAmount(salary, 10000000)}Cr`;
    }
    if (salary >= 100000) {
      return `${symbol}${scaledAmount(salary, 100000)}L`;
    }
  } else {
    if (salary >= 1000000000) {
      return `${symbol}${scaledAmount(salary, 1000000000)}B`;
    }
    if (salary >= 1000000) {
      return `${symbol}${scaledAmount(salary, 1000000)}M`;
    }
  }
  if (salary >= 1000) {
    return `${symbol}${scaledAmount(salary, 1000)}K`;
  }
  return `${symbol}${salary}`;
};

export const getEmployerPlanFeaturesData = (
  plan: CreatePlanResponse,
  isForTable = true,
) => {
  const getInitialText = (count?: number) =>
    count === PLAN_UNLIMITED_COUNT
      ? 'Unlimited'
      : count
        ? `Upto ${count ?? 0}`
        : '';
  const data: { isAvailable: boolean; text: string }[] = [];

  const isJobPlan = [
    EmployerPlanType.SingleJob,
    EmployerPlanType.TrialSingleJob,
    EmployerPlanType.FreeSingleJob,
  ].includes((plan.plan_type as any) ?? '');

  if (!isJobPlan) {
    data.push({
      isAvailable: !!plan.jobs_per_month,
      text: `${getInitialText(plan.jobs_per_month)} jobs per month`,
    });
  }
  data.push({
    isAvailable: !!plan.applicant_views_per_job,
    text: `${getInitialText(plan.applicant_views_per_job)} applicant views per job`,
  });
  data.push({
    isAvailable: !!plan.resume_download_per_job,
    text: `${getInitialText(plan.resume_download_per_job)} resume downloads per job`,
  });
  data.push({
    isAvailable: !!plan.ai_analysis_per_job,
    text: `${getInitialText(plan.ai_analysis_per_job)} AI Resume Analysis Per Job`,
  });
  if (!isJobPlan) {
    data.push({
      isAvailable: !!plan.db_resume_upload_per_month,
      text: `${getInitialText(plan.db_resume_upload_per_month)} Resume Xray per month`,
    });
  }
  data.push({
    isAvailable: !!plan.db_resume_download_per_job,
    text: `${getInitialText(plan.db_resume_download_per_job)} Database Resume Access/ job`,
  });
  data.push({
    isAvailable: plan.premium_applicant_filters ?? false,
    text: 'Premium Applicant Filters',
  });
  data.push({
    isAvailable: plan.bulk_actions ?? false,
    text: 'Bulk Actions',
  });
  data.push({
    isAvailable: true,
    text: plan.enhanced_reach
      ? isForTable
        ? plan.enhanced_reach
        : `${plan.enhanced_reach} enhanced reach`
      : '',
  });
  data.push({
    isAvailable: !!plan.invite_users,
    text: `${getInitialText(plan.invite_users)} invite users per job`,
  });
  data.push({
    isAvailable: true,
    text: plan.support
      ? isForTable
        ? plan.support
        : `${plan.support} support`
      : '',
  });

  return data;
};

export const getOrgStatusData = (
  orgData: GetOrganizationListSub | null,
): OrgStatusData | null => {
  if (!orgData || orgData.org_user_role !== 'Owner') {
    return null;
  }

  let data = ORG_STATUS_PROMT_DATA.get(orgData.status ?? '');

  if (data) {
    return {
      ...data,
      rejection_reason: orgData.rejection_reason ?? 'Complete your profile',
      status: orgData.status ?? '',
    };
  }

  if (
    !orgData.company?.image_url ||
    orgData.company?.image_url?.trim().length === 0
  ) {
    data = ORG_STATUS_PROMT_DATA.get('LogoMissing')!;
    return {
      ...data,
      rejection_reason:
        'Your business logo is required for verification. Please upload a valid logo to proceed',
      status: 'LogoMissing',
    };
  }
  return null;
};

export function getShortLongDateDiff(
  from: Date,
  to: Date,
): { short: string; long: string } {
  const years = differenceInYears(to, from);
  const months = differenceInMonths(to, from);
  const days = differenceInDays(to, from);
  const hours = differenceInHours(to, from);
  const minutes = differenceInMinutes(to, from);
  const seconds = differenceInSeconds(to, from);
  let short = 'now';
  let long = 'now';
  if (years !== 0) {
    short = `${Math.abs(years)}Y`;
    long = `${Math.abs(years)} year${years === 1 ? '' : 's'} ago`;
  } else if (months !== 0) {
    short = `${Math.abs(months)}M`;
    long = `${Math.abs(months)} month${months === 1 ? '' : 's'} ago`;
  } else if (days !== 0) {
    short = `${Math.abs(days)}D`;
    long = `${Math.abs(days)} day${days === 1 ? '' : 's'} ago`;
  } else if (hours !== 0) {
    short = `${Math.abs(hours)}H`;
    long = `${Math.abs(hours)} hour${hours === 1 ? '' : 's'} ago`;
  } else if (minutes !== 0) {
    short = `${Math.abs(minutes)}m`;
    long = `${Math.abs(minutes)} minute${minutes === 1 ? '' : 's'} ago`;
  } else if (seconds !== 0) {
    short = `${Math.abs(seconds)}s`;
    long = `${Math.abs(seconds)} second${seconds === 1 ? '' : 's'} ago`;
  }
  return { short, long };
}

export function getDuration(
  startDate: string | Date,
  endDate: string | Date,
  daysGreaterThan: number = 0,
): string {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffMs = end.getTime() - start.getTime();

  if (diffMs <= 0) return '0 mins';

  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > daysGreaterThan)
    return `${diffDays} day${diffDays > 1 ? 's' : ''}`;
  if (diffHours > 0) return `${diffHours} hr${diffHours > 1 ? 's' : ''}`;
  if (diffMins > 0) return `${diffMins} min${diffMins > 1 ? 's' : ''}`;
  return `${diffSecs} sec`;
}

export function formatDateDMY(dateString: string) {
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  };
  return date.toLocaleDateString('en-GB', options);
}

export const getCompetitionTimeState = (
  regStart: string,
  regEnd: string,
  compStart: string,
  compEnd: string,
  isRegistered: boolean = false,
): CompetitionTimeState => {
  const current = new Date();

  const regStartDate = parseUTCDate(regStart);
  const regEndDate = parseUTCDate(regEnd);
  const compStartDate = parseUTCDate(compStart);
  const compEndDate = parseUTCDate(compEnd);

  if (regStartDate && isBefore(current, regStartDate)) {
    return 'RegistrationStarts';
  }

  if (regEndDate && isBefore(current, regEndDate)) {
    if (!isRegistered) {
      return 'RegistrationEnds';
    }
    if (compStartDate && isAfter(current, compStartDate)) {
      return 'CompetitionEnds';
    }
  }

  if (compStartDate && isBefore(current, compStartDate)) {
    return 'CompetitionStarts';
  }

  if (compEndDate && isBefore(current, compEndDate)) {
    return 'CompetitionEnds';
  }

  return 'CompetitionEnded';
};

export const markEventOnCalendar = (
  startDate: string,
  endDate: string,
  name: string,
  details: string,
) => {
  const formatGoogleDate = (date: string) =>
    format(convertToUTC(new Date(date)), "yyyyMMdd'T'HHmmss'X'");

  const startStr = formatGoogleDate(startDate);
  const endStr = formatGoogleDate(endDate);

  const url = `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
    name,
  )}&dates=${startStr}/${endStr}&details=${encodeURIComponent(
    details,
  )}&sf=true&output=xml`;

  window.open(url, '_blank');
};

export const isDateBeforeOrEqual = (date?: string, dateToCompare?: string) =>
  isBefore(date ?? new Date(), dateToCompare ?? new Date()) ||
  isEqual(date ?? new Date(), dateToCompare ?? new Date());

export const getPlanInfo = (
  plan: CreatePlanResponse,
  orgCurrentPlan?: CreatePlanResponse,
) => {
  const isFreePlan = [
    EmployerPlanType.FreeSingleJob,
    EmployerPlanType.FreeWholeOrganization,
  ].includes((plan.plan_type as any) ?? '');

  const isJobPlan = [
    EmployerPlanType.FreeSingleJob,
    EmployerPlanType.SingleJob,
    EmployerPlanType.TrialSingleJob,
  ].includes((plan.plan_type as any) ?? '');

  const canExtend = !isFreePlan && !isJobPlan && orgCurrentPlan?.id === plan.id;

  const canUpgrade =
    !isFreePlan &&
    !isJobPlan &&
    orgCurrentPlan?.id !== plan.id &&
    ![
      EmployerPlanType.FreeWholeOrganization.toString(),
      EmployerPlanType.NoPlan.toString(),
    ].includes(orgCurrentPlan?.plan_type ?? '') &&
    (orgCurrentPlan?.jobs_per_month ?? 0) < (plan.jobs_per_month ?? 0);

  const isButtonDisabled =
    isFreePlan ||
    (!isJobPlan &&
      (orgCurrentPlan?.jobs_per_month ?? 0) >= (plan.jobs_per_month ?? 0) &&
      !canExtend);

  return { isFreePlan, isButtonDisabled, canUpgrade, isJobPlan, canExtend };
};

export const getExpiryData = ({
  createdAt,
  expiresIn,
}: {
  createdAt: string;
  expiresIn?: number | null;
}) => {
  const createdAtDate = parseUTCDate(createdAt);
  const now = new Date();

  if (!createdAtDate) {
    return {
      createdAtMoment: null,
      expiryText: 'Never',
      isExpired: false,
    };
  }

  // If expiresIn is null, treat as never expires
  if (!expiresIn) {
    return {
      createdAtMoment: createdAtDate,
      expiryText: 'Never',
      isExpired: false,
    };
  }

  const expiryDate = addDays(createdAtDate, expiresIn);

  let expiryText: string;

  if (isSameDay(createdAtDate, expiryDate)) {
    expiryText = 'Today';
  } else {
    const diffDays = differenceInCalendarDays(expiryDate, createdAtDate);
    expiryText = diffDays === 1 ? '1 day' : `${diffDays} days`;
  }

  const isExpired = isAfter(now, endOfDay(expiryDate));

  if (isExpired) {
    expiryText = 'Expired';
  }

  return {
    createdAtMoment: createdAtDate,
    expiryText,
    isExpired,
  };
};

export const jobDatePosted = (date: string) => {
  const postedDate = parseUTCDate(date);
  if (!postedDate) return '';
  const now = new Date();
  const diffInDays = differenceInDays(now, postedDate);
  const diffInWeeks = differenceInWeeks(now, postedDate);
  if (isSameDay(postedDate, now)) {
    return 'Posted today';
  }
  if (isSameDay(postedDate, subDays(now, 1))) {
    return 'Posted yesterday';
  }
  if (diffInDays <= 6) {
    return 'Posted this week';
  }
  if (diffInWeeks >= 1 && diffInWeeks <= 4) {
    return `Posted ${diffInWeeks} week${diffInWeeks > 1 ? 's' : ''} ago`;
  }
  return `Posted ${formatDistanceToNow(date, {
    addSuffix: true,
  }).replaceAll('about', '')}`;
};

export const getButtonData = (
  competitionDetails: GetAllCompetitionsSub | GetCompetitionListCardSub,
  timeState: CompetitionTimeState,
  isCardButton?: boolean,
): ButtonData => {
  const details = competitionDetails as GetAllCompetitionsSub;
  const associatedWithJobs = !!competitionDetails?.total_associated_companies;
  if (timeState === 'CompetitionEnds' && details.is_submitted && isCardButton) {
    return {
      label: 'Submitted',
      icon: null,
      variant: 'AlreadyDone',
    };
  }

  if (
    timeState === 'CompetitionEnded' &&
    competitionDetails.is_registered &&
    !details.is_submitted &&
    isCardButton
  ) {
    return {
      label: 'Participated',
      icon: null,
      variant: 'AlreadyDone',
    };
  }
  if (timeState === 'RegistrationStarts') {
    return {
      label: 'Register now',
      icon: null,
      variant: 'NotAllowed',
    };
  }

  if (timeState === 'RegistrationEnds' && !competitionDetails.is_registered) {
    return {
      label: 'Register now',
      icon: null,
      variant: 'Allowed',
    };
  }

  if (
    (timeState === 'RegistrationEnds' || timeState === 'CompetitionStarts') &&
    competitionDetails.is_registered
  ) {
    return {
      label: `You're in!`,
      icon: CheckCircleRoundedIcon,
      variant: 'AlreadyDone',
    };
  }

  if (timeState === 'CompetitionStarts') {
    return {
      label: 'Register now',
      icon: null,
      variant: 'NotAllowed',
    };
  }

  if (timeState === 'CompetitionEnds' && !details.is_submitted) {
    const isSubmissionType =
      competitionDetails.type_of_competition === 'Showcase' ||
      competitionDetails.type_of_competition === 'CodeIt';
    if (isSubmissionType && !isCardButton && !associatedWithJobs) {
      // Registered participants can submit while the window is open.
      if (competitionDetails.is_registered) {
        return {
          label:
            competitionDetails.type_of_competition === 'CodeIt'
              ? 'Submit code'
              : 'Submit work',
          icon: ArrowCircleRightOutlinedIcon,
          variant: 'Allowed',
        };
      }
      // Not registered: only actionable if the registration window is still open;
      // once registration has closed, non-participants can't submit.
      const regEnd = parseUTCDate(competitionDetails.reg_end_date);
      const registrationOpen = !!regEnd && regEnd.getTime() > Date.now();
      if (registrationOpen) {
        return {
          label: 'Register to submit',
          icon: ArrowCircleRightOutlinedIcon,
          variant: 'Allowed',
        };
      }
      return {
        label: 'Registration closed',
        icon: null,
        variant: 'NotAllowed',
      };
    }
    return {
      label:
        associatedWithJobs || isCardButton
          ? details.is_quiz_started
            ? 'Resume Challenge'
            : 'Start Challenge'
          : details.is_quiz_started
            ? 'Resume SnapQuiz'
            : 'Start SnapQuiz',
      icon: ArrowCircleRightOutlinedIcon,
      variant: competitionDetails.is_registered ? 'Allowed' : 'NotAllowed',
    };
  }

  if (timeState === 'CompetitionEnded') {
    // Registered participants keep access to the results area after the
    // competition ends so they can view their result and claim rewards —
    // job-linked competitions included. Non-participants stay locked out.
    if (competitionDetails.is_registered) {
      return {
        label: 'View Result',
        icon: VisibilityOutlinedIcon,
        variant: 'Allowed',
      };
    }
    return {
      label: 'Finished',
      icon: null,
      variant: 'NotAllowed',
    };
  }

  return {
    label: details.is_submitted
      ? associatedWithJobs
        ? 'Submission Completed'
        : 'View Submissions & Results'
      : 'View Results',
    icon: associatedWithJobs ? CheckCircleRoundedIcon : VisibilityOutlinedIcon,
    variant: competitionDetails.is_registered ? 'Allowed' : 'NotAllowed',
  };
};

export const getAttemptStatus = (data: AttemptHistory) => {
  if (!data.is_attempt_complete) {
    return null;
  }

  if (data.is_attempt_correct) {
    return 'correct';
  }

  if (data.question_skipped) {
    return 'skipped';
  }

  if (data.timeout) {
    return 'timeout';
  }

  return 'incorrect';
};

export const getJobCTC = (
  // salary_type came back nullable on some newer admin schemas (e.g. JobReviewSub);
  // other fields still line up, so we only broaden the one property.
  job: Omit<CandidateJobDetails, 'salary_type'> & {
    salary_type?: string | null;
  },
) => {
  const jobType = job?.job_type ?? '';
  const internshipType = job?.internship_type ?? '';
  const salFrequency = job?.salary_frequency ?? '';

  const minSalaryValue = removeCommas(job?.minimum_salary ?? 0);
  const maxSalaryValue = removeCommas(job?.maximum_salary ?? 0);

  const minSalary = getFormattedSalary(minSalaryValue, job?.currency);
  const maxSalary = getFormattedSalary(maxSalaryValue, job?.currency);

  // Internship check
  if (jobType === 'Internship' && internshipType === 'Unpaid') {
    return 'Unpaid';
  }

  // Variable / performance-based pay: amount-less, shows its own label
  if (job?.salary_type === 'Variable') {
    return 'Variable';
  }

  // Scraped jobs often carry no period; "/month" turned ₹10L a year into "₹10L /month".
  const frequency =
    SALARY_FREQUENCY.find((val) => val.id === salFrequency)?.shortLabel ?? '';
  const withFrequency = (amount: string) =>
    frequency ? `${amount} ${frequency}` : amount;

  if (job?.salary_type === 'Negotiable') {
    return 'Negotiable';
  }

  // No usable band. A Fixed salary is one amount that ingest may store in either
  // column. An external ad with no pay never said "negotiable", so it returns ''
  // and callers leave the salary out; our own postings keep reading Negotiable.
  if (!minSalaryValue || !maxSalaryValue) {
    const single = minSalaryValue || maxSalaryValue;
    if (job?.salary_type === 'Fixed' && single) {
      return withFrequency(getFormattedSalary(single, job?.currency));
    }
    return job?.is_external ? '' : 'Negotiable';
  }

  if (minSalary === maxSalary) {
    return withFrequency(minSalary);
  }

  return withFrequency(`${minSalary} - ${maxSalary}`);
};

/**
 * Single source of truth for rendering a job's experience requirement.
 * `maximum_experience` of 0/null means "no upper bound", so a min-only range
 * reads "N+ Years" rather than the historic "N - 0 Years" mis-render.
 */
export function formatExperienceRange(
  min?: number | null,
  max?: number | null,
): string {
  const lo = min ?? 0;
  const hi = max ?? 0;
  if (lo <= 0 && hi <= 0) return 'Any experience';
  if (hi > lo) return `${lo} - ${hi} Years`;
  // Equal min and max (e.g. AI-drafted jobs where both come out the same) is an
  // exact requirement, not open-ended — "3 Years", not "3+ Years".
  if (hi === lo) return `${lo} Years`;
  // Min set with no (or open-ended) max — also the safe fallback for any
  // legacy row where max was stored below min.
  return `${lo}+ Years`;
}

export function getJobExperienceLabel(job: CandidateJobDetails): string | null {
  const jobType = job?.job_type ?? null;
  const jobDuration = job?.job_duration ?? 0;
  const minimumWorkingHours = job?.minimum_working_hours ?? 0;
  const maximumWorkingHours = job?.maximum_working_hours ?? 0;
  const minimumExperience = job?.minimum_experience ?? 0;
  const maximumExperience = job?.maximum_experience ?? 0;

  if (jobType === 'Internship' && jobDuration && jobDuration > 0) {
    return `${jobDuration} ${jobDuration === 1 ? 'month' : 'months'}`;
  }

  if (
    jobType === 'PartTime' &&
    (minimumWorkingHours || maximumWorkingHours || jobDuration)
  ) {
    if (minimumWorkingHours || maximumWorkingHours) {
      if (maximumWorkingHours && maximumWorkingHours !== minimumWorkingHours) {
        return `${minimumWorkingHours} - ${maximumWorkingHours} hrs/week`;
      }
      return `${minimumWorkingHours} hrs/week`;
    }
    if (jobDuration && jobDuration > 0) {
      return `${jobDuration} ${jobDuration === 1 ? 'month' : 'months'}`;
    }
  }

  // Both columns empty means the ad never stated a requirement, which is not the
  // same as stating none. The locals above default null to 0, so falling into the
  // range branch reads that absence as 0-0 and answers "Any experience" — a claim
  // about the employer's openness that nobody made, and one a candidate could act
  // on. Null instead, and each caller decides how to present the gap: the details
  // hero names it, the cards drop the line.
  if (job?.minimum_experience == null && job?.maximum_experience == null) {
    return null;
  }

  return formatExperienceRange(minimumExperience, maximumExperience);
}

// Matches both the job side (RequiredSkills) and the candidate side (SkillsLevel).
export type SkillLike = { id?: string; name?: string };

/**
 * Below this, the percentage tells a candidate "poor fit" rather than anything
 * useful, so surfaces hide the number (the have/to-learn split still shows —
 * that's actionable). The apply dialogs use their own, lower floor.
 */
export const JOB_MATCH_DISPLAY_FLOOR = 60;

export interface JobSkillMatch {
  /** Required skills the candidate already has. */
  matchedSkills: SkillLike[];
  /** Required skills the candidate is missing — what to learn next. */
  missingSkills: SkillLike[];
  matched: number;
  total: number;
  pct: number;
}

/**
 * Overlap between a job's required skills and the candidate's profile skills.
 * Null when either side is empty — there is no honest number to show then, and
 * a 0% would read as "bad fit" rather than "we don't know yet".
 */
export const getJobSkillMatch = (
  requiredSkills?: SkillLike[] | null,
  candidateSkills?: SkillLike[] | null,
): JobSkillMatch | null => {
  const required = requiredSkills ?? [];
  const owned = candidateSkills ?? [];
  if (!required.length || !owned.length) return null;

  const ids = new Set(owned.map((s) => s.id).filter(Boolean));
  const names = new Set(
    owned.map((s) => s.name?.trim().toLowerCase()).filter(Boolean),
  );
  const has = (skill: SkillLike) =>
    (!!skill.id && ids.has(skill.id)) ||
    (!!skill.name && names.has(skill.name.trim().toLowerCase()));

  const matchedSkills = required.filter(has);
  return {
    matchedSkills,
    missingSkills: required.filter((s) => !has(s)),
    matched: matchedSkills.length,
    total: required.length,
    pct: Math.round((matchedSkills.length / required.length) * 100),
  };
};

export const getWorkFlexibilityLabel = (
  workFlexibility: string | undefined,
  locationDetails?: { terms?: string[]; description?: string } | null,
  termCount: 1 | 2 | 3 = 2,
  locationFallback?: string,
): string => {
  if (workFlexibility === 'Remote') return 'WFH - Remote';
  const prefix = workFlexibility === 'Hybrid' ? 'Hybrid' : 'Onsite';
  const terms = locationDetails?.terms;
  let locationStr: string | undefined;
  if (termCount === 1) {
    locationStr = terms?.slice(-3, -2)?.[0] ?? terms?.[0];
  } else if (termCount === 3) {
    locationStr = terms?.slice(-3)?.join(', ') || locationDetails?.description;
  } else {
    locationStr = terms?.slice(-2)?.join(', ');
  }
  const display = locationStr || locationFallback;
  return display ? `${prefix} - ${display}` : prefix;
};

// Matches the generated API Location shape (terms/description are nullable).
type LocationLike = {
  terms?: string[] | null;
  description?: string | null;
} | null;

// Multi-office label: "Onsite - Bengaluru, Mumbai" (or with maxShown, "Onsite -
// Bengaluru +2"). Falls back to the single primary when no list is present.
export const getJobLocationsLabel = (
  workFlexibility: string | undefined,
  locationList?: LocationLike[] | null,
  primary?: LocationLike,
  maxShown = 0,
): string => {
  if (workFlexibility === 'Remote') return 'WFH - Remote';
  const list = (
    locationList && locationList.length
      ? locationList
      : primary
        ? [primary]
        : []
  ).filter(Boolean) as NonNullable<LocationLike>[];
  const prefix = workFlexibility === 'Hybrid' ? 'Hybrid' : 'Onsite';
  const cityOf = (loc: NonNullable<LocationLike>) =>
    loc.terms?.slice(-3, -2)?.[0] ??
    loc.terms?.[0] ??
    loc.description?.split(',')[0]?.trim();
  const cities = list.map(cityOf).filter(Boolean) as string[];
  if (!cities.length) return prefix;
  const shown = maxShown > 0 ? cities.slice(0, maxShown) : cities;
  const extra = cities.length - shown.length;
  return `${prefix} - ${shown.join(', ')}${extra > 0 ? ` +${extra}` : ''}`;
};

/**
 * The city out of a Google-style place. `terms` runs [city, …, state, country],
 * so with all three present the city is third from the end; otherwise it's the
 * first entry.
 */
const cityOfLocation = (loc: LocationLike): string => {
  const terms = loc?.terms;
  const city = terms?.slice(-3, -2)?.[0] ?? terms?.[0];
  if (city) return city.trim();
  return ((loc?.description || '').split(',')[0] || '').trim();
};

/**
 * Words that describe how you work, not where. Scraped locations are full of
 * them ("Remote, Remote, India"), and pairing one with the work mode would read
 * as "Remote · Remote".
 */
const NOT_A_PLACE =
  /^(remote|anywhere|wfh|work from home|worldwide|global|n\/?a)$/i;

/**
 * How employers separate one office from the next, and a place from the work
 * mode glued onto it: commas, pipes, slashes, semicolons, colons, breadcrumb
 * arrows, and a spaced dash ("Chennai, India - Remote").
 */
const LOCATION_SEGMENTS = /[,|/;:>]+|\s+[-–—]\s+/;

/**
 * Mode words stripped wherever they sit inside a segment rather than only when
 * the segment is exactly one — "India (Remote)", "Mumbai Remote" and
 * "India-Remote" all name a place with the mode stuck to it.
 *
 * 'anywhere' / 'worldwide' / 'global' are deliberately absent: those only mean
 * "not a place" as a whole segment, which NOT_A_PLACE already handles, and
 * stripping them mid-segment would maul real names like "Bonifacio Global City".
 */
const MODE_WORDS =
  /\b(remote[- ]?first|remote|wfh|work from home|home office|hybrid|on-?site|teleworker|telecommute)\b/gi;

/**
 * Parts of a postal address that are never the city. Enterprise ATS feeds hand
 * over the employer's full mailing address ("7000 Target Pkwy N,NCD-0375
 * Brooklyn Park,MN 55445"), and the street always sits in the first segment —
 * so "take the first segment that names a place" showed the street number as
 * the location on ~60 live listings.
 *
 * `st` and `ave` are deliberately absent: "St. Louis" and "St Petersburg" are
 * real cities, and no city is named "<something> Street", so the spelled-out
 * forms carry the rule without the false positives.
 */
const ADDRESS_WORDS =
  // Unicode-aware boundaries, not `\b`: JS word boundaries are ASCII-only, so
  // `\bph\b` matched inside "Hiệp Phước" and threw away a real city.
  //
  // The leading boundary is `(^|[^…])` rather than a `(?<!…)` lookbehind on
  // purpose. Vite 8's baseline target here already clears it, but this block
  // is kept byte-identical to landing's copy, where Next targets `safari 12`
  // and an unsupported regex literal is a *parse* error that takes the whole
  // chunk down rather than just this function.
  // Capturing the boundary shifts the match index; placeOfSegment adds the
  // group's length back to compensate.
  /(^|[^\p{L}\p{N}])(pkwy|parkway|blvd|boulevard|street|road|rd|drive|lane|suite|ste|floor|flr|unit|building|bldg|tower|block|plot|premises|campus|sez|phase|wing|marg|survey|sy|sec|stpi|gf|ff)(?![\p{L}\p{N}])/iu;

/**
 * "Brooklyn Park", "Menlo Park" and "Overland Park" are cities; a business park
 * is not — so only the qualified phrases are rejected, never the bare word.
 */
const BUSINESS_PARK =
  /(^|[^\p{L}\p{N}])(business|tech|technology|industrial|office|it|software)\s+park(?![\p{L}\p{N}])/iu;

/**
 * Country and Indian-state codes that enterprise ATS feeds prefix to the city
 * ("IND Hyderabad", "IN KA Bengaluru", "IN-TS-HYDERABAD-B3F7"). An open
 * "any short all-caps token" rule cannot be used here: it eats the first word
 * of "NEW DELHI", "VAN NUYS" and "PUNE ITEC".
 */
const GEO_CODE =
  '(?:IND?|USA?|UK|GBR?|SGP?|AE|UAE|PH[LP]?|MY[S]?|JPN?|CHN?|DEU?|FRA?|CAN?|AUS?|NL[D]?|IE|ES[P]?|IT[A]?|PL|BR[A]?|MX|ZA)';
const STATE_CODE =
  '(?:KA|HR|TS|TN|MH|UP|WB|DL|AP|GJ|RJ|KL|PB|OR|MP|CG|JH|BR|AS|GA|UK|HP)';
/** "IND Hyderabad", "IN KA Bengaluru", "KA Bangalore" — space-separated form. */
const GEO_CODE_PREFIX = new RegExp(
  `^(?:${GEO_CODE}\\s+)?(?:${STATE_CODE}\\s+)?(?=\\p{L})`,
  'u',
);
/**
 * "IND-CHENNAI", "IN-TS-HYDERABAD-…", "IN-Mumbai-MSO" — dash-separated form.
 * The trailing lookahead keeps "US-WA" and "CA-ON" whole: a two-character
 * remainder is a state code, not a city, so stripping the country leaves less
 * than the original said.
 */
const GEO_CODE_DASH_PREFIX = new RegExp(
  `^${GEO_CODE}(?:-${STATE_CODE})?-(?=[^-]{3})`,
);
/** A segment that is only a code: "IND", "IN-KA", "USA". Never the city. */
const GEO_CODE_ONLY = new RegExp(
  `^${GEO_CODE}(?:[\\s-]${STATE_CODE})?$|^${STATE_CODE}$`,
);

/**
 * The employer's legal name, which ATS feeds put in the location
 * ("IND, Levi Strauss (India) Pvt Ltd., Bengaluru").
 */
const COMPANY_WORDS =
  /(^|[^\p{L}\p{N}])(pvt|private|ltd|limited|llp|llc|inc|corp|corporation|gmbh)(?![\p{L}\p{N}])/iu;

/** Just a country. A city in a later segment beats it ("India - Bengaluru"),
 * so it is skipped on the strict pass and kept only as the lenient fallback. */
const COUNTRY_ONLY =
  /^(?:india|usa|united states|united kingdom|uk|singapore|canada|germany|australia|philippines|pakistan|israel|south korea|korea|japan|china|france|netherlands|ireland|spain|poland|brazil|mexico|uae|united arab emirates|sri lanka|bangladesh|nepal|malaysia|indonesia|vietnam|thailand|switzerland|sweden|italy|portugal|new zealand|south africa|taiwan|norway|denmark|finland|belgium|austria|egypt|nigeria|kenya)$/i;

/** A state on its own -- "India - Haryana - Gurgaon": the city comes after.
 * Skipped like a country on the strict pass; kept as the lenient fallback. */
const STATE_ONLY =
  /^(?:andhra pradesh|assam|bihar|chhattisgarh|goa|gujarat|haryana|himachal pradesh|jharkhand|karnataka|kerala|madhya pradesh|maharashtra|odisha|punjab|rajasthan|tamil nadu|telangana|uttar pradesh|uttarakhand|west bengal|central)$/i;

/** A region: the place when nothing narrower is named, so skipped only on the
 * strict pass, like a country ("Remote - EMEA" still reads EMEA). */
const REGION_ONLY =
  /^(?:nationwide|eu|europe|emea|apac|latam|south america|north america|east coast|west coast)$/i;

/** A team or a word left over from "Remote Based", "Full-Time" -- never a place. */
const NOT_A_SETTLEMENT =
  /^(?:engineering|marketing|sales|operations|finance|product|design|support|analytics|based|full|fully|time|full[- ]?time|part[- ]?time|country|field|region|distributed|multiple locations)$/i;

/**
 * A country named in full and dashed onto the city: "India-Bengaluru (Helios
 * Business Park)" -- 500+ live jobs. Unspaced, so the segment split leaves it
 * whole; the code forms ("IND-CHENNAI") are GEO_CODE_DASH_PREFIX's.
 */
const COUNTRY_DASH_PREFIX =
  /^(?:india|usa|united states|united kingdom|uk|singapore|canada|germany|australia)\s*[-–]\s*(?=\p{L})/iu;

/** A branch code prefixed to the city with no separator: "NCD-0375 Brooklyn Park". */
const BRANCH_CODE_PREFIX = /^[A-Z]{2,4}-[A-Z0-9]{2,8}\s+(?=\p{L})/u;

/**
 * A state and country glued to the city with spaces only: "Pune Maharashtra
 * India", "Bengaluru India". Delhi is left out of the states on purpose -- it
 * would cut "New Delhi India" down to "New".
 */
const TRAILING_INDIA =
  /\s+(?:(?:andhra pradesh|assam|bihar|chhattisgarh|goa|gujarat|haryana|himachal pradesh|jharkhand|karnataka|kerala|madhya pradesh|maharashtra|odisha|punjab|rajasthan|tamil nadu|telangana|uttar pradesh|uttarakhand|west bengal)\s+)?india$/i;

/** A postal code trailing the city: "Bangalore 540065", "Hyderabad - 500081". */
const TRAILING_POSTCODE = /[\s-–]+\d{4,6}$/;

/**
 * The place one segment of a free-text location names, or '' if it names none.
 *
 * `strict` additionally rejects segments that read as a street or building
 * rather than a settlement. It runs as a first pass over every segment; only if
 * no segment survives it does the caller retry leniently, so an address-shaped
 * string still yields something ("Mumbai Sidrah Building") rather than nothing.
 */
const placeOfSegment = (segment: string, strict: boolean): string => {
  const place = segment
    .replace(MODE_WORDS, ' ')
    // Punctuation the split didn't own — tildes, brackets, stray quotes.
    .replace(/[^\p{L}\p{N}\s.'&-]+/gu, ' ')
    // Joiners a stripped mode word left dangling ("India-Remote" -> "India-").
    .replace(/(^|\s)[-–—]+|[-–—]+(\s|$)/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (NOT_A_PLACE.test(place) || GEO_CODE_ONLY.test(place)) return '';
  if (COMPANY_WORDS.test(place)) return '';
  if (NOT_A_SETTLEMENT.test(place)) return '';
  if (
    strict &&
    (COUNTRY_ONLY.test(place) ||
      STATE_ONLY.test(place) ||
      REGION_ONLY.test(place))
  )
    return '';
  // A leading digit is a house, floor or suite number ("7000 Target Pkwy N",
  // "4th Floor …", "12C") — or a count ("2 Locations"). Never a city.
  if (/^\d/.test(place)) return '';
  // A bare unit code: "D-4", "E-145", "B7". No settlement is named this way.
  if (/^\p{L}{1,2}[-\s]?\d+\p{L}?$/u.test(place)) return '';
  // The boundary group's length is added back so the index lands on the address
  // word itself, exactly where a lookbehind would have put it — `search()` alone
  // would point at the separator and change what the lenient slice keeps.
  const addressMatch = ADDRESS_WORDS.exec(place);
  const addressPart = addressMatch
    ? addressMatch.index + addressMatch[1].length
    : -1;
  if (strict && (addressPart >= 0 || BUSINESS_PARK.test(place))) return '';
  // Lenient pass: nothing in the string looked like a settlement, so keep only
  // what precedes the address part rather than printing the whole mailing line
  // ("Mumbai Sidrah Building" -> "Mumbai Sidrah").
  const settlement = addressPart > 0 ? place.slice(0, addressPart) : place;
  const named = settlement
    .replace(TRAILING_POSTCODE, '')
    .replace(TRAILING_INDIA, '')
    .replace(GEO_CODE_DASH_PREFIX, '')
    .replace(COUNTRY_DASH_PREFIX, '')
    .replace(BRANCH_CODE_PREFIX, '')
    .replace(GEO_CODE_PREFIX, '')
    .trim();
  // What a stripped country leaves when no city was named: "Remote in India",
  // "Office India".
  if (/^(in|at|office|head office|hq)$/i.test(named)) return '';
  // A bare two-letter region ("QC", "TX") is the state, never the city.
  if (strict && /^[A-Z]{2}$/.test(named)) return '';
  return named.length < 2 ? '' : named;
};

type JobLocationFields = {
  work_flexibility?: string | null;
  job_location_details?: LocationLike;
  job_location_details_list?: LocationLike[] | null;
  external_location?: string | null;
  is_external?: boolean | null;
};

/**
 * Every city a job is offered in, most significant first. Aggregated jobs are
 * described by the employer's own free text, falling back to the structured
 * location when that text names no place; only our own jobs are described by
 * the structured location alone.
 */
export const getJobCities = (job: JobLocationFields): string[] => {
  const list = (
    job?.job_location_details_list?.length
      ? job.job_location_details_list
      : job?.job_location_details
        ? [job.job_location_details]
        : []
  ).filter(Boolean) as LocationLike[];
  const structured = list
    .map(cityOfLocation)
    .filter((city) => !!city && !NOT_A_PLACE.test(city));

  const external = (job?.external_location || '').trim();
  if (job?.is_external && external) {
    // Free text arrives as "Pune, MH, India", "India (Remote)", "USA | Remote",
    // "India-Bengaluru-Remote", and from enterprise ATS feeds as a full mailing
    // address. Take the first segment that names a settlement once the work
    // mode is stripped out of it — strictly first, so a street segment can't
    // win over the city two commas later, then leniently so an all-address
    // string still yields its best guess.
    const segments = external
      // A parenthetical is an aside about the mode or a list of other offices,
      // never the place itself — and it has to go before the split, so its own
      // commas don't become segment boundaries.
      .replace(/\([^)]*\)/g, ' ')
      .split(LOCATION_SEGMENTS);
    const city =
      segments.map((seg) => placeOfSegment(seg, true)).find(Boolean) ||
      segments.map((seg) => placeOfSegment(seg, false)).find(Boolean);
    // Only when the employer's text names no place at all does the structured
    // location get a turn. Nearly every aggregated job has one, and it is clean
    // ("{India}") exactly where the free text is not.
    return city ? [city] : structured;
  }
  return structured;
};

/** 'Remote' | 'Hybrid' | 'Onsite' — Onsite is the default when unspecified. */
export const getJobWorkMode = (job: JobLocationFields): string => {
  if (job?.work_flexibility === 'Remote') return 'Remote';
  return job?.work_flexibility === 'Hybrid' ? 'Hybrid' : 'Onsite';
};

/**
 * "Pune · Remote" / "Bengaluru, Mumbai +2 · Onsite", or the bare work mode when
 * no city is known. City and work mode only — state and country are noise at
 * card and tile size, and the full address is in the job description.
 *
 * Deliberately identical to the landing site's helper of the same name (see
 * resume-landing-page src/helpers/index.ts), because the two disagreeing about
 * where a job is was a real bug: a scraped posting whose `work_flexibility` said
 * 'Remote' while its `external_location` said "Pune, MH, India" read as
 * "WFH - Remote" on the public page and "Pune, MH, India" here.
 *
 * `getJobLocationsLabel` above is the older "Onsite - City" label still used by
 * the employer and dashboard surfaces; this one is for the candidate jobs flow.
 */
export const formatJobLocationLabel = (
  job: JobLocationFields,
  maxShown = 0,
): string => {
  const cities = getJobCities(job);
  const mode = getJobWorkMode(job);
  if (!cities.length) return mode;
  const shown = maxShown > 0 ? cities.slice(0, maxShown) : cities;
  const extra = cities.length - shown.length;
  return `${shown.join(', ')}${extra > 0 ? ` +${extra}` : ''} · ${mode}`;
};

export const getSocialShareData = (
  shareUrl: string,
  shareText: string = '',
) => {
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedText = encodeURIComponent(shareText);

  return [
    {
      label: 'Linkedin',
      icon: LinkedinColoredIcon,
      // `feed/?shareActive=true&text=` opens LinkedIn's post composer with the
      // text + URL prefilled. `share-offsite` ignores custom text (it only
      // scrapes OG data from the URL), so it can't be used here.
      url: `https://www.linkedin.com/feed/?shareActive=true&mini=true&text=${encodedText}${encodedUrl}`,
    },
    {
      label: 'Twitter',
      icon: TwitterColoredIcon,
      url: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`,
    },
    {
      label: 'Whatsapp',
      icon: WhatsappColoredIcon,
      url: `https://api.whatsapp.com/send?text=${encodedText}%20${encodedUrl}`,
    },
    {
      label: 'Instagram',
      icon: InstagramColoredIcon,
      url: 'https://www.instagram.com/',
    },
    {
      label: 'Facebook',
      icon: FacebookColoredIcon,
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    },
    {
      label: 'Email',
      icon: EmailColoredIcon,
      url: `mailto:?subject=Competition&body=${encodedText}%20${encodedUrl}`,
    },
  ] as const;
};

export const getStreakFeedbackFromCounter = (attemptCounter: {
  correct: number;
  incorrect: number;
  timeout: number;
  skipped: number;
}): FeedbackMessage | undefined => {
  const { correct, incorrect, timeout, skipped } = attemptCounter;

  if (correct > 0) {
    const index = (correct - 1) % 5;
    const pool =
      PLAY_STREAK_FEEDBACK.positive[index] ?? PLAY_STREAK_FEEDBACK.positive[0];
    return pool[Math.floor(Math.random() * pool.length)];
  }

  if (incorrect > 0) {
    const index = (incorrect - 1) % 5;
    const pool =
      PLAY_STREAK_FEEDBACK.negative[index] ?? PLAY_STREAK_FEEDBACK.negative[0];
    return pool[Math.floor(Math.random() * pool.length)];
  }

  if (timeout > 0) {
    const pool = PLAY_STREAK_FEEDBACK.timedout;
    return pool[Math.floor(Math.random() * pool.length)];
  }

  if (skipped > 0) {
    const pool = PLAY_STREAK_FEEDBACK.skipped;
    return pool[Math.floor(Math.random() * pool.length)];
  }

  return undefined;
};

export const getEmployerResumeDownloadSettings =
  (): ResumeDownloadSettings => ({
    hideContactDetails: JSON.parse(
      localStorage.getItem(LS_KEY_HIDE_CONTACT_DETAILS_FROM_RESUME) || 'false',
    ),
    showWhiteLabelling: JSON.parse(
      localStorage.getItem(LS_KEY_WHITE_LABEL_RESUME) || 'false',
    ),
    type: (localStorage.getItem(LS_KEY_WHITE_LABEL_RESUME_TYPE) ??
      'both') as ResumeDownloadSettings['type'],
    showDBResumeConfirmationDialog: JSON.parse(
      localStorage.getItem(LS_KEY_SHOW_DB_RESUME_DOWNLOAD_CONFIRMATION) ??
        'true',
    ),
    downloadOriginalResume: JSON.parse(
      localStorage.getItem(LS_KEY_DOWNLOAD_ORIGINAL_APPLICANT_RESUME) ??
        'false',
    ),
  });

export const getPublicRedirectPathname = (currentPathname: string) => {
  const competitionPathmatch = matchPath(
    '/competition/:competitionAlias',
    currentPathname,
  );

  if (competitionPathmatch) {
    return `/public-competition/${competitionPathmatch.params.competitionAlias}`;
  }

  const jobSearchPathmatch = matchPath(
    '/job-search/:jobAlias',
    currentPathname,
  );

  if (jobSearchPathmatch) {
    return `/public-job-search/${jobSearchPathmatch.params.jobAlias}`;
  }

  const certificatePathmatch = matchPath(
    '/certificate/:certificateAlias',
    currentPathname,
  );

  if (certificatePathmatch) {
    return `/public-certificate/${certificatePathmatch.params.certificateAlias}`;
  }

  const companyJobsPagePathmatch = matchPath(
    '/company-jobs/:companyAlias',
    currentPathname,
  );

  if (companyJobsPagePathmatch) {
    return `/public-company-jobs/${companyJobsPagePathmatch.params.companyAlias}`;
  }

  // Ambassador HQ falls back to the public campus-ambassador landing page.
  if (matchPath('/campus-ambassador/hq', currentPathname)) {
    return '/campus-ambassador';
  }

  // The landing site has no faculty sign-in, so '/' would strand a professor.
  if (matchPath('/faculty/*', currentPathname)) {
    return '/login';
  }

  return '/';
};

/**
 * A skill percentile as shown to people: "Top 0.3%", "Top 5.2%", "Top 18%".
 * Rounded up past 10%, so the label never claims more than the number.
 */
export const formatTopPercent = (topPercent: number) =>
  `Top ${topPercent >= 10 ? Math.ceil(topPercent) : Number(Math.max(topPercent, 0.1).toFixed(1))}%`;

/**
 * Whether a skill's tested field is big enough to state its size. Below
 * PERCENTILE_MIN_POOL a total like "27 tested" reads as an empty room, so rank
 * copy names the skill instead of counting the people in it.
 */
export const showsRankPoolSize = (poolSize?: number | null): boolean =>
  (poolSize ?? 0) >= PERCENTILE_MIN_POOL;

/** In a small field the goal is a place, not a share: the top 10. */
export const RANK_SMALL_FIELD_GOAL = 10;

/** What a rank is out of: "of 1,233 tested candidates", or where, when small. */
export const rankContext = (
  poolSize?: number | null,
  skillName?: string | null,
): string =>
  showsRankPoolSize(poolSize)
    ? `of ${(poolSize ?? 0).toLocaleString('en-IN')} tested candidates`
    : `in ${skillName || 'this skill'} on Abekus`;

/** The faces row's caption — the crowd's size only when it's worth stating. */
export const rankCrowdLabel = (
  poolSize: number | null | undefined,
  others: number,
  skillName?: string | null,
): string =>
  showsRankPoolSize(poolSize)
    ? `+${others.toLocaleString('en-IN')} others tested`
    : `Others are learning ${skillName || 'this skill'}`;

/** "Top N%" framing stops at the top half: "Top 99%" reads as a boast about
 * coming last. Below it, a rank is stated as its position. */
const RANK_PERCENT_FRAMING_MAX = 50;

type SkillRankLike = {
  position?: number | null;
  top_percent?: number | null;
} | null;

/** Whether a rank reads as "Top N%" (big enough pool, top half) — the cases
 * that also get a bar. */
export const isPercentRank = (rank?: SkillRankLike): boolean =>
  rank?.top_percent != null && rank.top_percent <= RANK_PERCENT_FRAMING_MAX;

/**
 * A skill rank as every screen shows it: "Top 3%" when isPercentRank, else the
 * leaderboard position, "#8" (the backend only sends `top_percent` once the
 * tested pool is big enough for one). Null when there is no rank to show.
 */
export const formatSkillRank = (rank?: SkillRankLike): string | null => {
  if (isPercentRank(rank)) return formatTopPercent(rank!.top_percent!);
  if (rank?.position != null)
    return `#${rank.position.toLocaleString('en-IN')}`;
  return null;
};

export const formatNumberToThousand = (num: number) => {
  if (num >= 1000) {
    return `${Math.floor(num / 100) / 10}k+`;
  }
  if (num >= 100) {
    return `${Math.floor(num / 100) * 100}+`;
  }
  return num.toString();
};

// Compact display of an EXACT value: 2560 → "2.56k", 282298 → "282.3k",
// 1_250_000 → "1.25M". Up to 2 decimals, trailing zeros trimmed. Use for
// credits / XP / score where the precise value lives in a tooltip. (Distinct
// from formatNumberToThousand, which appends "+" for approximate counts.)
export const formatCompact = (n?: number | null): string => {
  if (!n) return '0';
  const abs = Math.abs(n);
  const trim = (v: number) => parseFloat(v.toFixed(2)).toString();
  if (abs >= 1_000_000) return `${trim(n / 1_000_000)}M`;
  if (abs >= 1_000) return `${trim(n / 1_000)}k`;
  return String(n);
};

export const getNoticePeriodOptions = () => {
  const options = new Array(8).fill(0)?.map((_, index) => ({
    value: index + 1,
    label: `${index + 1} month`,
  }));

  options.push({ value: 0, label: 'Not on Notice Period' });
  return options;
};

export const getSkillTestCardButtonType = (
  data: GetAllDomainMockTestSub,
): SkillTestCardButtonType => {
  let buttonType = SkillTestCardButtonType.BuyTest;

  if (data.is_template && !data.is_unlocked) {
    buttonType = SkillTestCardButtonType.UnlockTest;
  } else if (!data.is_template) {
    if (data.is_completed) {
      buttonType = SkillTestCardButtonType.ViewResult;
    } else if (data.started_on) {
      buttonType = SkillTestCardButtonType.ResumeTest;
    } else {
      buttonType = SkillTestCardButtonType.StartTest;
    }
  }

  return buttonType;
};

// function to generate hash for uploaded files
export const getFileHash = async (data: ArrayBuffer) => {
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hash = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  return hash;
};

export function buildInviteProfilePayload({
  filters,
  inviteProfiles,
  page_no,
  page_size,
}: InviteApplicantPayloadParams): ProfileMatchParam & Record<string, any> {
  const matchingLocations =
    filters?.locations?.map((loc) => loc?.description!) ?? [];

  const matchingSkillsIds =
    filters?.skills?.map((skill) => skill?.id as string) ?? [];

  return {
    page_no: page_no ?? (inviteProfiles?.current_page ?? 0) + 1,
    page_size: page_size ?? inviteProfiles?.page_size ?? 10,
    is_invited:
      filters.userStatus === 'invited'
        ? true
        : filters.userStatus === 'new'
          ? false
          : undefined,
    is_rejected: filters.userStatus === 'rejected' ? true : undefined,
    matching_locations:
      matchingLocations.length > 0 ? matchingLocations : undefined,
    matching_skills_ids:
      matchingSkillsIds.length > 0 ? matchingSkillsIds : undefined,
    min_experience: filters?.experience?.min || undefined,
    max_experience: filters?.experience?.max || undefined,
    min_percentile: filters?.percentile?.min || undefined,
    max_percentile: filters?.percentile?.max || undefined,
    ...(filters?.passingYear?.min != null && {
      min_passing_year: filters.passingYear.min,
    }),
    ...(filters?.passingYear?.max != null && {
      max_passing_year: filters.passingYear.max,
    }),
    ...(filters?.grade?.min != null && { min_grade: filters.grade.min }),
    ...(filters?.grade?.max != null && { max_grade: filters.grade.max }),
    ...(filters?.collegeTier?.length && { college_tier: filters.collegeTier }),
    ...(filters?.colleges?.length && {
      college_ids: filters.colleges.map((c) => c.id),
    }),
    ...(filters?.sortBy === 'aiFit' && {
      sort_by: 'resume_analysis_overall_score',
    }),
  } as any;
}

export const getCompetitionStatusData = (
  data: GetAllCompetitionsSub | GetCompetitionListCardSub,
) => {
  const current = convertToUTC(new Date());
  if (isAfter(current, convertToUTC(data.end_date ?? ''))) {
    return { text: 'Finished', color: '#25507B' };
  }

  if (isAfter(current, convertToUTC(data.start_date ?? ''))) {
    return { text: 'Ongoing', color: '#388E3C' };
  }

  return { text: 'Upcoming', color: '#FFB800' };
};

export const formatApplicantListFilters = (
  filter: InputFilters,
  jobType?: string,
): ApplicantListParam => {
  const isInternship = jobType === 'Internship';

  const convertToAllOrBool = (value: string) => {
    if (value === 'true') {
      return true;
    }
    if (value === 'false') {
      return false;
    }
    return undefined;
  };

  return {
    applicant_status: filter.applicant_status.map((item) => item.value),
    applicant_tag: filter.applicant_tag.map((item) => item.value),
    notice_period:
      filter.notice_period.value === 'all'
        ? undefined
        : Number(filter.notice_period.value),
    is_applicant_seen: convertToAllOrBool(filter.is_applicant_seen.value),
    // The options hold months (WORK_EXPERIENCE_OPTIONS); the API compares years.
    min_experience:
      isInternship || !filter.experience.min
        ? undefined
        : filter.experience.min.value / 12,
    max_experience:
      isInternship || !filter.experience.max
        ? undefined
        : filter.experience.max.value / 12,
    min_work_experience_months: isInternship
      ? filter.internship_experience.min?.value
      : undefined,
    max_work_experience_months: isInternship
      ? filter.internship_experience.max?.value
      : undefined,
    max_grade: filter.cgpa.max?.value,
    min_grade: filter.cgpa.min?.value,
    min_passing_year: filter.passing_year.min?.value,
    max_passing_year: filter.passing_year.max?.value,
    min_assessment_score: filter.assessment_score.min?.value,
    max_assessment_score: filter.assessment_score.max?.value,
    min_rating: filter.profile_rating.min
      ? filter.profile_rating.min.value * 2
      : undefined,
    max_rating: filter.profile_rating.max
      ? filter.profile_rating.max.value * 2
      : undefined,
    min_percentile: filter.match_percentile.min?.value,
    max_percentile: filter.match_percentile.max?.value,
    resume_uploaded: convertToAllOrBool(filter.resume_uploaded.value),
    matching_locations: filter.matching_locations
      ?.filter((item) => item.location_details?.id)
      .map((item) => item.location_details?.id!),
    sort_by: filter.sort_by,
    sort_order: filter.sort_order,
    matching_skills: filter.matching_skills?.map((item) => item.value),
    missing_skills: filter.missing_skills?.map((item) => item.value),
    college_ids: filter.colleges?.map((item) => item.value),
    job_role_ids: filter.job_roles?.map((item) => item.value),
    required_skills: filter.skill_score?.map((item) => ({
      skill_id: item.value,
      min_accuracy: item.min_accuracy,
      min_level: item.min_level,
      min_total_questions_solved: item.min_questions_solved,
    })),
    user_search_query: filter.user_search_query,
    assessment_screening: filter.assessment_screening?.length
      ? filter.assessment_screening
      : undefined,
  };
};

export const isApplicantReviewFilterApplied = (
  filters: InputFilters,
  jobType?: string,
) => {
  const isInternship = jobType === 'Internship';
  const arrFilter: (keyof InputFilters)[] = [
    'applicant_status',
    'applicant_tag',
    'matching_skills',
    'missing_skills',
    'skill_score',
    'assessment_screening',
  ];
  if (arrFilter.some((key) => !!(filters[key] as any[] | null)?.length)) {
    return true;
  }

  const rangeFilter: (keyof InputFilters)[] = [
    isInternship ? 'internship_experience' : 'experience',
    'assessment_score',
    'profile_rating',
    'match_percentile',
    'cgpa',
  ];

  if (
    rangeFilter.some(
      (key) =>
        (filters[key] as MinMax<any>).max || (filters[key] as MinMax<any>).min,
    )
  ) {
    return true;
  }

  // if (filters.matching_locations?.[0].location_details) {
  //   return true;
  // }

  const radioFilters: (keyof InputFilters)[] = [
    'resume_uploaded',
    'is_applicant_seen',
  ];

  if (radioFilters.some((key) => (filters[key] as Option).value !== 'all')) {
    return true;
  }
  return false;
};

// Number of active filter groups — drives the count badge on the Filters button.
// Sort and free-text search are NOT filters and are excluded.
export const countApplicantReviewActiveFilters = (
  filters: InputFilters,
  jobType?: string,
): number => {
  const isInternship = jobType === 'Internship';
  let count = 0;

  const arrFilters: (keyof InputFilters)[] = [
    'applicant_status',
    'applicant_tag',
    'matching_skills',
    'missing_skills',
    'skill_score',
    'assessment_screening',
  ];
  arrFilters.forEach((key) => {
    if ((filters[key] as any[] | null)?.length) count += 1;
  });

  const rangeFilters: (keyof InputFilters)[] = [
    isInternship ? 'internship_experience' : 'experience',
    'assessment_score',
    'profile_rating',
    'match_percentile',
    'cgpa',
    'passing_year',
  ];
  rangeFilters.forEach((key) => {
    const range = filters[key] as MinMax<any> | undefined;
    if (range && (range.max || range.min)) count += 1;
  });

  const radioFilters: (keyof InputFilters)[] = [
    'resume_uploaded',
    'is_applicant_seen',
  ];
  radioFilters.forEach((key) => {
    const opt = filters[key] as Option | undefined;
    if (opt && opt.value !== 'all') count += 1;
  });

  return count;
};

export const formatJobCount = (count = 0) => {
  if (count < 3) {
    return count;
  }
  if (count < 5) {
    return '3+';
  }
  const even = count % 2 === 0 ? count : count - 1;
  return count % 2 === 0 ? `${even}` : `${even}+`;
};

export const csvToJson = (
  csv: string,
  allowedKeys: string[],
  metadataKey: string | null,
  numberKeys?: string[],
): any[] => {
  const lines = csv.trim().split('\n');
  const headers = lines[0]
    .split(',')
    .map((h) => h.trim().toLowerCase().replace(/\s+/g, '_'));

  const items = lines.slice(1).map((line) => {
    const values = line
      .split(/,(?=(?:[^"]*"[^"]*")*[^"]*$)/)
      .map((value) => value.replaceAll(/"/g, ''));
    const item: Record<string, any> = {};
    const metadata: Record<string, any> = {};

    headers.forEach((header, i) => {
      const value = values[i] ?? '';

      if (allowedKeys.includes(header)) {
        if (numberKeys && numberKeys.includes(header)) {
          if (!isNaN(Number(value))) {
            item[header] = Number(value);
          }
        } else {
          item[header] = value;
        }
      } else if (metadataKey) {
        metadata[header] = value;
      }
    });

    if (metadataKey && Object.keys(metadata).length > 0) {
      item[metadataKey] = metadata;
    }

    return item;
  });

  return items;
};

export const formatCompetitinoDateRange = (start: string, end: string) => {
  const startDate = parseUTCDate(start);
  const endDate = parseUTCDate(end);

  if (!startDate || !endDate) return '';

  const sameMonth = startDate.getMonth() === endDate.getMonth();
  const sameYear = startDate.getFullYear() === endDate.getFullYear();

  if (sameMonth && sameYear) {
    return `${format(startDate, 'd')}-${format(
      endDate,
      'd',
    )} ${format(startDate, "MMM''yy")}`;
  }

  if (!sameMonth && sameYear) {
    return `${format(startDate, 'd MMM')}-${format(endDate, "d MMM''yy")}`;
  }

  return `${format(startDate, "d MMM''yy")}-${format(endDate, "d MMM''yy")}`;
};

export const getAssociatedJobsProfileData = (
  profileMatchPercentage: number,
) => {
  let background = '';
  let label = '';

  if (profileMatchPercentage > 95) {
    background =
      'linear-gradient(90deg, rgba(0, 0, 0, 0.12) 0%, rgba(0, 0, 0, 0.00) 100%), #4CAF50';
    label = 'Perfect Match';
  } else if (profileMatchPercentage > 90) {
    background =
      'linear-gradient(90deg, rgba(0, 0, 0, 0.12) 0%, rgba(0, 0, 0, 0.00) 100%), #7BC74D';
    label = 'Very Good Match';
  } else if (profileMatchPercentage > 80) {
    background =
      'linear-gradient(90deg, rgba(0, 0, 0, 0.12) 0%, rgba(0, 0, 0, 0.00) 100%), #E6C74C';
    label = 'Good Match';
  } else if (profileMatchPercentage > 70) {
    label = '70% match';
  } else if (profileMatchPercentage > 60) {
    label = '60% match';
  } else if (profileMatchPercentage > 50) {
    label = '50% match';
  } else {
    label = '< 50% match';
  }

  return {
    background,
    label,
  };
};

// Widened from SkillIDNameWeight so callers holding plain `{ name }` shapes can
// share this instead of re-deriving a chip budget.
// The server rounds the applicant count to the nearest 50; below that the number
// is too thin to be worth surfacing at all.
const APPLICANTS_BUCKET = 50;

/** "150+ applicants", or '' when the count is too small to mean anything. */
export const getApplicantsLabel = (rounded?: number | null) => {
  const count = rounded ?? 0;
  if (count < APPLICANTS_BUCKET) return '';
  return `${Math.floor(count / APPLICANTS_BUCKET) * APPLICANTS_BUCKET}+ applicants`;
};

export const getSkillCountToShow = (
  skills: { name?: string | null }[],
  charLimit: number,
) => {
  let charCount = 0;
  let skillCount = 0;
  const skillsToShow: string[] = [];
  for (let i = 0; i < skills.length; i += 1) {
    if (charCount + (skills[i].name?.length || 0) < charLimit) {
      charCount += skills[i].name?.length ?? 0;
      skillCount += 1;
      skillsToShow.push(skills[i].name ?? '');
    } else {
      if (skillCount === 0) {
        skillsToShow.push(`${skills[i].name?.slice(0, charLimit)}...`);
      }
      break;
    }
  }

  return skillsToShow;
};

export const getStateOrCountry = (terms?: string[]) => {
  if (!terms || terms.length === 0) return 'In Office';
  if (terms.length >= 2) {
    return terms[terms.length - 2];
  }
  return terms[0];
};

export const convertSkillArrayToWeightMap = (
  arr: SkillIDNameWeight[] | undefined,
) => {
  const result: Record<string, number> = {};

  arr?.forEach((s) => {
    if (s.id && s.weight !== undefined) {
      result[s.id] = s.weight;
    }
  });

  return result;
};

export const buildQuestionOrderMap = (
  questionsArr: GetCompetitionQuestionsSub[],
  // isRandomized: boolean,
) => {
  const map: Record<string, number | null> = {};

  questionsArr.forEach((q, index) => {
    if (!q?.id) return;
    // If randomized AND this question is NOT fixed → null
    // if (isRandomized && !q.is_pinned) {
    //   map[q.id] = null;
    // } else {
    //   // In normal mode OR fixed in random mode → keep index
    map[q.id] = index;
    // }
  });

  return map;
};

export const applyAttemptUpdate = (
  prev: MultiplayerSessionProgressResponse | null,
  data: AttemptNotifResponse,
): MultiplayerSessionProgressResponse | null => {
  if (!prev) return prev;

  let scoreDelta = 0;
  let pointsDelta = 0;

  const updatedUsersProgress = prev?.users_progress?.map((userProgress) => {
    if (userProgress?.user?.id !== data.user_id) {
      return userProgress;
    }

    const updatedAttempts = userProgress?.attempts?.map((attempt) => {
      if (attempt.question_id !== data.question_id) {
        return attempt;
      }
      scoreDelta = (data.attempt_score ?? 0) - (attempt.attempt_score ?? 0);
      pointsDelta = (data.round_points ?? 0) - (attempt.round_points ?? 0);
      return {
        ...attempt,
        ...data,
        is_attempt_complete: true,
      };
    });

    return {
      ...userProgress,
      attempts: updatedAttempts,
      total_score: roundToFractionDigits(
        (userProgress.total_score ?? 0) + scoreDelta,
        2,
      ),
      // Points move the ladder, so they have to ride the socket event too —
      // otherwise every standing reads 0 until the next full progress fetch.
      total_points: roundToFractionDigits(
        (userProgress.total_points ?? 0) + pointsDelta,
        2,
      ),
    };
  });

  return {
    ...prev,
    users_progress: updatedUsersProgress,
  };
};

export const checkUserPremium = (premiumProfileExpiresOn?: string) => {
  if (!premiumProfileExpiresOn) return false;
  const expiresAt = parseUTCDate(premiumProfileExpiresOn);
  if (!expiresAt) return false;
  return expiresAt.getTime() > Date.now();
};

// True only for the legacy "Unlimited" plan, which waives all credits. The new
// Standard plan is premium but has a finite credit balance (topped up on
// purchase), so credit-related UI must NOT treat it as unlimited. A NULL/blank
// plan on an active premium row is a grandfathered legacy buyer → Unlimited.
export const checkUserUnlimitedCredits = (
  premiumProfileExpiresOn?: string,
  premiumPlanType?: string | null,
) =>
  checkUserPremium(premiumProfileExpiresOn) && premiumPlanType !== 'Standard';

export const getMultiplayerPhase = ({
  attemptId,
  lastGameId,
  startStatistics,
}: {
  attemptId?: string | null;
  lastGameId?: string | null;
  startStatistics: MultiplayerSessionStartStatisticsResponse | null;
}): MultiplayerPhase => {
  const {
    is_user_session_started: isUserSessionStarted,
    room_state: roomState,
    is_user_session_completed: isUserSessionCompleted,
  } = startStatistics ?? {};

  // WAITING means no round of theirs is running (e.g. back button into a game
  // they left), so a stale attemptId in the URL must not reopen it.
  if (attemptId && roomState !== 'WAITING') {
    return 'PLAY';
  }
  if (lastGameId) {
    return 'RESULT';
  }
  if (!isUserSessionStarted && roomState === 'IN_PROGRESS') {
    return 'WAITING';
  }
  if (isUserSessionCompleted || roomState === 'RESULT') {
    return 'RESULT';
  }
  if (roomState === 'IN_PROGRESS') {
    return 'PLAY';
  }
  return 'WAITING';
};

export const qualityLabels: Record<number, { text: string; color: string }> = {
  0: { text: 'Needs Improvement', color: '#EF4444' },
  1: { text: 'Medium', color: '#F59E0B' },
  2: { text: 'Good', color: '#4caf50' },
};

export const formatTimeToHMS = (seconds?: number | null) => {
  if (!seconds && seconds !== 0) return '-';

  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Number((seconds % 60).toFixed(2));

  const parts = [];

  if (hrs > 0) parts.push(`${hrs} hr`);
  if (mins > 0) parts.push(`${mins} min`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs} sec`);

  return parts.join(' ');
};

export function getDeviceType() {
  if (typeof window === 'undefined') return null;

  const ua = navigator.userAgent || navigator.vendor || '';
  const platform = navigator.platform || '';

  // iOS (including older iPads/iPhones/iPods)
  if (/iPhone|iPad|iPod/i.test(ua)) {
    return 'ios';
  }

  // iPadOS 13+ can pretend to be Mac
  if (
    platform === 'MacIntel' &&
    typeof navigator.maxTouchPoints === 'number' &&
    navigator.maxTouchPoints > 1
  ) {
    return 'ios';
  }

  if (/Android/i.test(ua)) {
    return 'android';
  }

  if (/Win/i.test(platform) || /Windows/i.test(ua)) {
    return 'windows';
  }

  if (/Mac/i.test(platform) || /Macintosh/i.test(ua)) {
    return 'mac';
  }

  if (/Linux/i.test(platform) || /Linux/i.test(ua)) {
    return 'linux';
  }

  return null;
}

/**
 * The device as the backend names it (`XDeviceType`), for the `x-device-type`
 * header and the socket handshake — both of which have to agree, or the same
 * browser is one device to the API and another to presence.
 */
export function getHeaderDeviceType(): HeaderDeviceType | null {
  switch (getDeviceType()) {
    case 'android':
      return HeaderDeviceType.ANDROID_WEB;
    case 'ios':
      return HeaderDeviceType.IOS_WEB;
    case 'linux':
    case 'mac':
    case 'windows':
      return HeaderDeviceType.DWEB;
    default:
      return null;
  }
}

/**
 * `copy` maps a server error key to human text. Without it the key is only
 * title-cased, which turns something the user can act on ("resume_still_
 * processing") into what reads like a crash ("Resume Still Processing").
 */
export const formatToFetchError = (
  err: any,
  copy?: Record<string, string>,
): { error: FetchBaseQueryError } => {
  const key = err?.response?.data?.error;
  return {
    error: {
      status: 'CUSTOM_ERROR',
      error: (typeof key === 'string' && copy?.[key]) || getFormattedErr(key),
      // The server's key, kept beside the copy so a caller can act on the
      // reason (isAiCreditShortfall) rather than match the words shown.
      data: key,
    },
  };
};

/**
 * The server refused an AI spend on the balance. A client gate checks first,
 * but the balance it reads can be stale (spent in another tab, a missed
 * socket push), so the refusal has to open the same top-up, not an error.
 */
export const isAiCreditShortfall = (err: any) =>
  // `data` from formatToFetchError; `message` from downloadPdfResponse.
  err?.data === 'not_enough_ai_credits' ||
  err?.message === 'not_enough_ai_credits';

// Loads the file just to read its intrinsic pixel dimensions — never appended
// to the DOM, so the object URL is revoked as soon as we have naturalWidth/Height.
export const getImageDimensions = (
  file: File,
): Promise<{ width: number; height: number }> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Unable to read image dimensions'));
    };
    image.src = url;
  });

// A small tolerance absorbs off-by-one-pixel exports from crop/design tools
// without letting a genuinely mismatched image through.
export const matchesAspectRatio = (
  file: File,
  aspectRatio: number,
  tolerance = 0.02,
): Promise<boolean> =>
  getImageDimensions(file).then(
    ({ width, height }) =>
      Math.abs(width / height - aspectRatio) <= aspectRatio * tolerance,
  );

export const getHiringAgentState = (
  timeState: CompetitionTimeState | null,
  calibration: CalibrationStatusResponse | null,
  startDate: string | null,
  maxSkills: number,
): HiringAgentState => {
  const dateLabel = (date?: string | null) => {
    const parsed = date ? parseUTCDate(date) : null;
    return parsed ? format(parsed, 'd MMM') : 'soon';
  };

  // Ended wins over "choose skills" / "complete your profile" — there's nothing
  // left to pick or fix. has_matching_skills is live-computed, so it can stay
  // false for the whole event and would otherwise show a dead-end CTA forever.
  if (timeState === 'CompetitionEnded') {
    return {
      status: 'This event has ended. Your agent is no longer applying.',
      cta: 'View your agent',
      tone: 'done',
    };
  }

  if (!calibration) {
    return {
      status: 'Open your agent to choose skills and track your progress.',
      cta: 'Open your agent',
      tone: 'waiting',
    };
  }

  if (!calibration.has_matching_skills) {
    return {
      status:
        'Your profile has no skills matching this event yet. Add them so your agent can match you to roles.',
      cta: 'Complete your profile',
      tone: 'action',
    };
  }

  if (calibration.is_complete) {
    const applied = calibration.ai_applied_jobs?.length ?? 0;
    return {
      status: applied
        ? `All skills calibrated. Your agent has applied you to ${applied} ${applied === 1 ? 'role' : 'roles'}.`
        : 'All skills calibrated. Your agent is scanning open roles and will apply for you.',
      cta: applied ? 'See applied roles' : 'View your agent',
      tone: 'done',
    };
  }

  const skills = calibration.skills ?? [];
  const hasPicked = (calibration.selected_skill_ids?.length ?? 0) > 0;
  const calibrated = skills.filter((s) => s.is_calibrated).length;
  const started = skills.some((s) => (s.questions_served ?? 0) > 0);

  // Registered but the event hasn't opened yet — they can still pick skills.
  if (timeState === 'CompetitionStarts') {
    const opens = dateLabel(startDate);
    return hasPicked
      ? {
          status: `You're set — ${skills.length} ${skills.length === 1 ? 'skill' : 'skills'} chosen. Calibration opens ${opens}.`,
          cta: 'Review your skills',
          tone: 'waiting',
        }
      : {
          status: `Pick your skills now so you're ready the moment calibration opens ${opens}.`,
          cta: 'Pick your skills',
          tone: 'action',
        };
  }

  if (!hasPicked) {
    return {
      status: `Pick up to ${maxSkills} skills to show employers what you're best at.`,
      cta: 'Pick your skills',
      tone: 'action',
    };
  }

  if (started) {
    return {
      status: `Calibration in progress — ${calibrated} of ${skills.length} skills done. Keep going to unlock more matches.`,
      cta: 'Continue calibration',
      tone: 'action',
    };
  }

  return {
    status: `Your ${skills.length} ${skills.length === 1 ? 'skill is' : 'skills are'} ready. Start calibrating so your agent can apply for you.`,
    cta: 'Start calibrating',
    tone: 'action',
  };
};
